// Collection of transaction functions

/**
 * Module of functions in charge of transaction consolidation and manipulation.
 * All table operations are managed using Denfo.js
 */

// import Tensor Flow
import * as tf from "@tensorflow/tfjs"

// Import Danfo
import * as dfd from "danfojs"



// Import date functions
import * as dateFun from "./../utils/dateFunctions"  

// Global constants
import * as con from "./../GlobalConstants"

import { getBandsConstructionFunction, getSummaryBands } from "./bandFunctions";

import { getDefaultValue} from '../templates/Types'
import { average } from "../GlobalFunctions";
import { counterpartiesNames, translateParameter } from "./translateFunctions";
import { addMonths } from '../utils/dateFunctions'
import { getOptionAssessmentData } from "./optionFunctions";
import { getMarketValue } from "../store/actions/market";

// Epsylon
const epsylon = 1e-14


export const getSearchCompareFunction = (id, colType, targetValue) =>
{

    let fun = (ob) => ob[id] === targetValue    

    if(colType === con.MONEY)
        fun = (ob) => ob[id] === targetValue
    else if(colType === con.TEXT)
        fun = (ob) => ob[id].toUpperCase().includes(targetValue.toUpperCase())

    return(fun)

}

/**
 * Builds the filter function for transactions based on filter parameters.
 *
 * @param {Object|null} params Filter parameters stored in application state
 * @returns {(ob: Object) => boolean} Filter function used by TransactionTable
 */
export const getFilterFunction = (params) => {
  // If no filter parameters exist, show everything
  if (!params) return (_) => true

  const {
    include_include_expired: includeExpired = true,
    include_active: includeActive = true,
    include_include_prepay: includePrepay = true,
    include_purchases: includePurchases = true,
    include_sales: includeSales = true
  } = params

  return (ob) => {

    let show = true

    if (ob[con.STATE] === con.EXPIRED)
      show = show && includeExpired

    if (ob[con.STATE] === con.ACTIVE)
      show = show && includeActive

    if (ob[con.STATE] === con.PREPAYED)
      show = show && includePrepay

    if (ob[con.COVERAGE_TYPE] === con.BUY)
      show = show && includePurchases

    if (ob[con.COVERAGE_TYPE] === con.SELL)
      show = show && includeSales

    return show
  }
}


// Transactions with summary
export const processTransactionsWithSummary = async (currentDate,
                                            spot,
                                            scenarioGenerationFun,
                                            modelParameters, 
                                            exposures,
                                            coveragesForward,
                                            optionCoverages,
                                            accounts,
                                            modelApplicationParameters) =>
{        
        // Adds aditional options info (like delta)
        const option_list = Object.values(optionCoverages)
        const referenceDate = currentDate
        const option_assessment_rate = spot
        let options = await getOptionAssessmentData({option_list, referenceDate, option_assessment_rate})
        options = options[con.DATA][con.DATA] !== undefined ? options[con.DATA][con.DATA][con.COVERAGES_OPTION] : undefined

        let df_trans = executeProcessTransactions(currentDate, spot,scenarioGenerationFun, modelParameters, exposures, coveragesForward, options, accounts, modelApplicationParameters)
        let resp = []
        let summary = {}


        if(df_trans !== null)
        {   
            resp = dfd.toJSON(df_trans, { download: false }) 
            resp.forEach((ob,i) => ob[con.ID] = i)
            summary = await createSummary(df_trans, modelParameters)                    
        }

        return([resp, summary])
}




// Method that gets total vars
export const getTotalVars = async (currentDate, 
                                    spot,
                                    scenarioGenerationFun,
                                    modelParameters, 
                                    exposures,
                                    coveragesForward,
                                    optionCoverages,
                                    accounts,
                                    modelApplicationParameters) =>
{

// Declares the var columns
let varColumns = ['var'].concat(con.BANDS.map((b) => `${b}_${con.VAR}`))

// Extracts transactions summary
let trans_df = executeProcessTransactions(currentDate,spot,scenarioGenerationFun, modelParameters, exposures, coveragesForward, optionCoverages, accounts, modelApplicationParameters)

if(trans_df === null)
    return(null)
    
return( Object.assign({}, ...varColumns.map((col) => ({[col]: trans_df[col].sum()}))))


}

export const sortTransactionKeysByColumn = (transactionsDic, col, order) =>
{
    let keys = Object.keys(transactionsDic)
    if(col === null)
        return(keys)

    keys = keys.sort((k1, k2) => {
        if (transactionsDic[k1][col] < transactionsDic[k2][col]) {
            return(order === con.ASCENDING ? -1 : 1)
          }
          if (transactionsDic[k1][col] > transactionsDic[k2][col]) {
            return(order === con.ASCENDING ? 1 : -1)
          }
            return(0)
        });

    return(keys)
}

export const selectTransactionsByPage = (transArray, page) =>
{
    return(transArray.slice(page*con.MAX_ROWS_PER_PAGE, Math.min(transArray.length, (page+1)*con.MAX_ROWS_PER_PAGE)))
}

export const getTotalTransactionPages = (totalTransactions) =>
{
    return(Math.floor(Math.max(0,totalTransactions -1)/con.MAX_ROWS_PER_PAGE))
}


/**
 * Main method
 * The current flow to consolidate the transactions is as follows:
 * 1. Filters by only active exposures and coverages
 * 2. Merge exposures and forward coverages into a single DataFrame
 * 3. Uses the money in accounts to remove first exposures
 * 4. Attaches the dollar value (VaR) to the data Frame
 * 5. Takes dates to the end of the desired periodicity
 * 6. Groups transactions by date
 * 7. Applies the over coverage scheme
 * 8. Constructs Opportunistic VaR 
 * 9. Constructs VaR by Band
 * 10. Groups summary table by Month
 * 11. Attaches Position in bands
 */
const executeProcessTransactions = (current_date,
                                    spot,
                                    scenarioGenerationFun,
                                    modelParameters, 
                                    exposures,
                                    coveragesForward,
                                    optionCoverages,
                                    accounts,
                                    modelApplicationParameters) =>
{


    if(Object.keys(accounts).length === 0)
        return(createEmptyDataFrame())

    // Extracts variables
    // Model
    let clientType = modelParameters[con.CLIENT_TYPE]
    let periodicity = modelParameters[con.PERIODICITY]
    let term_periodicity = modelParameters[con.TERM_PERIODICITY]   
    let average_for_percentage = modelParameters[con.AVERAGE_FOR_PERCENTAGE]     
    let bands = modelParameters[con.MID_BAND_VALUES]
    let bands_with = modelParameters[con.BAND_WIDTH]
    let passthrough = 0

    let [term_in_months, monthly_bands] = adjustBandToPeriodicity(periodicity, term_periodicity, current_date, bands)
    
    // Application
    let carryOverCoverage = modelApplicationParameters[con.CARRY_OVER_COVERAGE]
    let visualizationPeriodicity = modelApplicationParameters[con.VISUALIZATION_PERIODICITY]


    // Filters
    exposures = filterExposures(current_date, term_in_months, exposures)
    let [directCoveragesForward, counterCoveragesForward] = filterFWDCoverages(current_date, term_in_months, clientType, coveragesForward)
    let [directCall, directPut, counterBuyCall, counterSellCall, counterBuyPut, counterSellPut] = filterOptionCoverages(current_date, term_in_months, optionCoverages, clientType)

    if(exposures.length === 0 && directCoveragesForward.length === 0 && counterCoveragesForward.length === 0 &&
        directCall.length === 0 && directPut.length === 0 && counterBuyCall.length === 0 && counterSellCall.length === 0 &&
        counterBuyPut.length === 0 && counterSellPut.length === 0)
            return(createEmptyDataFrame())

    // Adds mock exposure and coverages (so that the table is computed)
    exposures.push(createMockExposure(current_date))
    directCoveragesForward.push(createMockFwdCoverage(current_date))
    counterCoveragesForward.push(createMockFwdCoverage(current_date))
    directCall.push(createMockOptionCoverage(current_date))
    directPut.push(createMockOptionCoverage(current_date))
    counterBuyCall.push(createMockOptionCoverage(current_date))
    counterSellCall.push(createMockOptionCoverage(current_date))
    counterBuyPut.push(createMockOptionCoverage(current_date))
    counterSellPut.push(createMockOptionCoverage(current_date))

    // Merges
    let trans_df = mergeExposuresAndCoverages(clientType, current_date, periodicity, term_in_months, exposures, directCoveragesForward, counterCoveragesForward,
                                              directCall, directPut, counterBuyCall, counterSellCall, counterBuyPut, counterSellPut, spot)                      

    // Removes exposures with account balances
    trans_df = removeExposures(trans_df, accounts)

    // Attaches scenario 
    trans_df = attachDollarScenario(current_date, spot, trans_df, scenarioGenerationFun) 

    // Consolidate options and VaR
    trans_df = optionVARConsolidation(trans_df)

    // Adjusts periodicity
    trans_df = adjustPeriodicity(trans_df, periodicity)

    // Groups  
    trans_df = groupByDate(trans_df) 


    // Over coverage
    if(carryOverCoverage)
        trans_df = applyOverCoverageScheme(trans_df) 


    // LEGACY
    // // Attaches real_position
    // trans_df = attachRealPosition(trans_df)  


    // Adds the opportunistic Var
    trans_df = addOpportunisticVar(trans_df, spot, clientType, passthrough)  

    
    // Adds Bands
    trans_df = addVarByBands(current_date, trans_df, spot, clientType, average_for_percentage, monthly_bands, bands_with)
        
    // Converts periodicity to month
    if(visualizationPeriodicity !== periodicity)
    {
        trans_df = adjustPeriodicity(trans_df, visualizationPeriodicity)
        trans_df = groupByDate(trans_df)   
    }


    trans_df = attachPositionInBands(trans_df)

    // Adds Period
    trans_df = trans_df.addColumn(con.PERIOD, [...Array(trans_df.shape[0]).keys()])
    
    return(trans_df)

}

/**
 * Adjusts the input bands to align with the specified periodicity (DAILY, WEEKLY, MONTHLY),
 * generating a monthly average band series based on the desired frequency.
 *
 * @param {string} periodicity - Desired frequency: "DAY", "WEEK", or "MONTH".
 * @param {number} term_periodicity - Number of periods to visualize ahead, based on the given periodicity.
 * @param {Date} current_date - Starting point for the band timeline (usually today's date).
 * @param {number[]} bands - Target bands for each period (e.g., [month 0, month 1, ..., month N]).
 *
 * @returns {[number, number[]]} - A tuple containing:
 *   1. term_in_months: Horizon of the projection in months.
 *   2. monthly_bands: Array of average monthly bands adjusted to the specified periodicity.
 */

const adjustBandToPeriodicity = (periodicity, term_periodicity, current_date, bands) => {
    const copyBands = [...bands];  // Prevent mutating original bands
    const lastBand = copyBands[copyBands.length - 1];

    let term_in_months;
    let monthly_bands;

    const dateRange = [];
    const newBands = [];

    // Utility function to safely get the next band, fallback to last known band
    const getNextBand = () => copyBands.length > 0 ? copyBands.shift() : lastBand;

    switch (periodicity) {
        case con.PERIODICITY_WEEK:
            term_in_months = Math.ceil(term_periodicity / 4);  // Approximate 4 weeks per month
            let endDateWeek = addMonths(current_date, term_in_months);

            while (current_date <= endDateWeek) {
                dateRange.push(current_date);

                if (current_date.weekday === 1) { // Monday = 1
                    newBands.push(getNextBand());
                } else {
                    const previousBand = newBands.length > 0 ? newBands[newBands.length - 1] : bands[0];
                    newBands.push(previousBand ?? lastBand);
                }

                current_date = dateFun.addDays(current_date, 1, true);
            }
            break;

        case con.PERIODICITY_DAY:
            term_in_months = Math.ceil(term_periodicity / 30); // Approximate 30 days per month
            let endDateDay = addMonths(current_date, term_in_months);

            while (current_date <= endDateDay) {
                dateRange.push(current_date);
                newBands.push(getNextBand());
                current_date = dateFun.addDays(current_date, 1, true);
            }
            break;

        case con.PERIODICITY_MONTH:
            term_in_months = term_periodicity;
            monthly_bands = bands;
            break;

        default:
            throw new Error(`Unsupported periodicity: ${periodicity}`);
    }

    // If dates were generated, aggregate to monthly average bands
    if (dateRange.length > 0) {
        let all_bands = dateRange.map((d, i) => ({
            [con.DATE]: dateFun.formatDate(d),
            band: newBands[i]
        }));

        all_bands = new dfd.DataFrame(all_bands);
        all_bands = adjustPeriodicity(all_bands, con.PERIODICITY_MONTH); // Resample to monthly
        all_bands = all_bands.groupby([con.DATE]).mean();

        monthly_bands = all_bands["band_mean"].values;
    }

    return [term_in_months, monthly_bands];
};




/**
 * Filters exposure records to include only those with expiration dates
 * that fall within a given horizon, starting from the current date and
 * extending to the end of the projected term (in months).
 *
 * @param {Date} current_date - The reference date (typically today).
 * @param {number} term_in_months - Number of months to project into the future.
 * @param {Object} exposures - A dictionary of exposures, where each value is an object containing an "expiration_date" property.
 *
 * @returns {Object[]} - An array of filtered exposure objects whose expiration dates fall within the projection window.
 */

const filterExposures = (current_date, term_in_months, exposures) => {
    // Define the date range for filtering: from current date to end of the term
    const minDate = current_date;
    const maxDate = dateFun.takeDateToEndOfPeriodicity(
        dateFun.addMonths(current_date, term_in_months),
        con.PERIODICITY_MONTH
    );

    // Convert object values to array and filter exposures within the date range
    const filteredExposures = Object.values(exposures).filter((row) => {
        const expDate = dateFun.parseDate(row.expiration_date);
        return expDate > minDate && expDate <= maxDate;
    });

    return filteredExposures;
};


/**
 * Filters coverage records to include only those that are active within a specified date range.
 *
 * The range goes from the `current_date` to the end of the month that contains the last projected month.
 *
 * @param {Date} current_date - The starting reference date (typically today).
 * @param {number} term_in_months - Number of months into the future to consider for coverage expiration.
 * @param {Object[]|Object} coverages - An array or object of coverage records, each with an 'expiration_date'.
 *
 * @returns {Object[]} - Array of coverage objects whose expiration date falls within the calculated range.
 */
const filterCoveragesByDateRange = (current_date, term_in_months, coverages) => {
    // Calculate the minimum and maximum dates for the range
    const min_date = current_date;
    const max_date = dateFun.takeDateToEndOfPeriodicity(
        dateFun.addMonths(current_date, term_in_months),
        con.PERIODICITY_MONTH
    );

    // Default to an empty object if coverages is undefined
    if (!coverages) {
        coverages = {};
    }

    // Filter coverages where expiration_date is within the range (exclusive of min, inclusive of max)
    const filteredCoverages = Object.values(coverages).filter(row => {
        const expiration = dateFun.parseDate(row.expiration_date);
        return expiration > min_date && expiration <= max_date;
    });

    return filteredCoverages;
};



/**
 * Filters forward coverages based on the date range and client type,
 * separating them into direct and counter coverages according to business logic.
 *
 * @param {Date} current_date - Reference date to start filtering (typically today's date).
 * @param {number} term_in_months - Number of months to project into the future.
 * @param {string} clientType - Type of client ("IMPORTER" or otherwise).
 * @param {Object[]} fwdCoverages - Array of forward coverage objects, each having a coverage type.
 *
 * @returns {[Object[], Object[]]} - Tuple with two arrays:
 *   1. directCoverages: Coverages matching the direct coverage type for the client.
 *   2. counterCoverages: Coverages matching the counter coverage type.
 */
const filterFWDCoverages = (current_date, term_in_months, clientType, fwdCoverages) => {
    // Filter coverages within the specified date range
    const filteredCoverages = filterCoveragesByDateRange(current_date, term_in_months, fwdCoverages);

    // Determine coverage types based on client type
    const directType = clientType === con.IMPORTER ? con.BUY : con.SELL;
    const indirectType = clientType === con.IMPORTER ? con.SELL : con.BUY;

    // Separate coverages into direct and counter categories
    const directCoverages = filteredCoverages.filter(coverage => coverage[con.COVERAGE_TYPE] === directType);
    const counterCoverages = filteredCoverages.filter(coverage => coverage[con.COVERAGE_TYPE] === indirectType);

    return [directCoverages, counterCoverages];
};


/**
 * Filters and classifies option coverages by type (CALL/PUT) and position (BUY/SELL),
 * according to the client's role (IMPORTER or EXPORTER), and within a defined date range.
 *
 * @param {Date} current_date - Starting reference date (typically today).
 * @param {number} term_in_months - Number of months ahead to consider for coverage expiration.
 * @param {Object[]|Object} optionCoverages - Option coverage records with expiration and classification fields.
 * @param {string} clientType - Client type, either con.IMPORTER or con.EXPORTER.
 *
 * @returns {[Object[], Object[], Object[], Object[], Object[], Object[]]} - Array of six coverage lists:
 *   [directCall, directPut, counterBuyCall, counterSellCall, counterBuyPut, counterSellPut]
 */
const filterOptionCoverages = (current_date, term_in_months, optionCoverages, clientType) => {
    // Step 1: Filter by expiration date range
    optionCoverages = filterCoveragesByDateRange(current_date, term_in_months, optionCoverages);

    // Step 2: Initialize classification buckets
    const directCall = [];
    const directPut = [];
    const counterBuyCall = [];
    const counterSellCall = [];
    const counterBuyPut = [];
    const counterSellPut = [];

    // Step 3: Categorize coverages based on client type and option characteristics
    optionCoverages.forEach(ob => {
        const isCall = ob[con.OPTION_TYPE] === con.CALL;
        const isPut = ob[con.OPTION_TYPE] === con.PUT;
        const isBuy = ob[con.COVERAGE_TYPE] === con.BUY;
        const isSell = ob[con.COVERAGE_TYPE] === con.SELL;

        if (clientType === con.IMPORTER) {
            if (isCall && isBuy) directCall.push(ob);
            else if (isCall && isSell) counterSellCall.push(ob);
            else if (isPut && isBuy) counterBuyPut.push(ob);
            else if (isPut && isSell) counterSellPut.push(ob);
        } else if (clientType === con.EXPORTER) {
            if (isPut && isBuy) directPut.push(ob);
            else if (isCall && isBuy) counterBuyCall.push(ob);
            else if (isCall && isSell) counterSellCall.push(ob);
            else if (isPut && isSell) counterSellPut.push(ob);
        }
    });

    // Step 4: Return structured classification
    return [
        directCall,
        directPut,
        counterBuyCall,
        counterSellCall,
        counterBuyPut,
        counterSellPut
    ];
};

/**
 * Process FWD coverages (direct or counter) by:
 *  - Renaming fields for consistency
 *  - Multiplying rate by amount (weighted rate)
 *  - Grouping by date and summing
 *  - Calculating weighted average rate by dividing weighted sum by total amount
 * 
 * @param {Array} coverages - Raw coverage data array
 * @param {string} rateCol - Column name for coverage rate in raw data
 * @param {string} amountOutCol - Desired output column name for amount
 * @param {string} rateOutCol - Desired output column name for rate
 * @returns {dfd.DataFrame} - Processed DataFrame with grouped and weighted average rates
 */
function processFWDCoverages(coverages, amountOutCol, rateOutCol) {
  // Rename fields and map to standard keys
  let df = new dfd.DataFrame(
    coverages.map(row => {
      const missingFields = [con.AMOUNT, con.RATE]
        .filter(field => row[field] === undefined || row[field] === null);
      if (missingFields.length > 0) {
        console.warn(
          `[processFWDCoverages] Registro con campo(s) nulo(s) ${missingFields.join(", ")} (se usará 0). Registro:`,
          row
        );
      }

      return {
        [con.DATE]: row[con.EXPIRATION_DATE],
        [amountOutCol]: row[con.AMOUNT] ?? 0,
        [rateOutCol]: row[con.RATE] ?? 0,
      };
    })
  );

  // Multiply rate by amount to get weighted rate
  df = setColumn(df, rateOutCol, df[rateOutCol].mul(df[amountOutCol]));

  // Group by date and sum amounts and weighted rates
  df = df.groupby([con.DATE])
    .sum()
    .rename({
      [`${amountOutCol}_sum`]: amountOutCol,
      [`${rateOutCol}_sum`]: rateOutCol,
    });

  // Avoid division by zero: replace 0 amounts with NaN to prevent Infinity rates
  const amounts = df[amountOutCol].values;
  for (let i = 0; i < amounts.length; i++) {
    if (amounts[i] === 0) {
      df.loc({ rows: [i], columns: [rateOutCol] }).values[0][0] = NaN;
    }
  }

  // Calculate weighted average rate = weighted sum / total amount
  df = setColumn(df, rateOutCol, df[rateOutCol].div(df[amountOutCol]));

  return df;
}

/**
 * Process a options coverages DataFrame with common operations:
 * - Extract and rename columns
 * - Multiply strike by delta amount
 * - Group by date and sum numeric columns
 * - Rename summed columns consistently
 * - Calculate weighted strike (strike * delta / delta sum)
 *
 * @param {DataFrame | Array} coverageData - Input raw data (array or dfd.DataFrame)
 * @param {Object} config - Configuration with column names
 * @param {string} config.deltaAmountCol - Column name for delta amount to be used in processed DataFrame
 * @param {string} config.amountRenamedCol - New column name for amount after renaming
 * @param {string} config.strikeRenamedCol - New column name for strike after renaming
 * @returns {DataFrame} Processed and grouped DataFrame with consistent column names
 */
function processOptionsCoverages(coverageData, config) {
  const { 
    deltaAmountCol,
    amountRenamedCol,
    strikeRenamedCol,
  } = config;

  // Step 1: Extract and rename columns, applying sign to premium gains
  let processed = coverageData.map(row => {
    const missingFields = [con.COVERAGES, con.AMOUNT, con.STRIKE, con.PREMIUM_GAINS]
      .filter(field => row[field] === undefined || row[field] === null);
    if (missingFields.length > 0) {
      console.warn(
        `[processOptionsCoverages] Registro con campo(s) nulo(s) ${missingFields.join(", ")} (se usará 0). Registro:`,
        row
      );
    }

    return {
      [con.DATE]: row[con.EXPIRATION_DATE],
      [deltaAmountCol]: row[con.COVERAGES] ?? 0,
      [amountRenamedCol]: row[con.AMOUNT] ?? 0,
      [strikeRenamedCol]: row[con.STRIKE] ?? 0,
      [con.PREMIUM_GAINS]: row[con.PREMIUM_GAINS] ?? 0,
    };
  });

  // Step 2: Convert to DataFrame if not already
  processed = new dfd.DataFrame(processed);

  // Step 3: Multiply strike by delta amount (weighted strike numerator)
  processed = setColumn(
    processed,
    strikeRenamedCol,
    processed[strikeRenamedCol].mul(processed[deltaAmountCol])
  );

  // Step 4: Group by date and sum all numeric columns
    processed = processed.groupby([con.DATE]).sum();

  // Step 5: Rename summed columns by removing the "_sum" suffix to keep names consistent
  const renameMap = {};
  processed.columns.forEach(col => {
  if (col.endsWith("_sum")) {
    const originalCol = col.slice(0, -4); // remove "_sum"
    renameMap[col] = originalCol;
  }
  });
  processed = processed.rename(renameMap);

  // Step 6: Calculate weighted strike (weighted strike numerator / total delta amount)
  processed = setColumn(
    processed,
    strikeRenamedCol,
    processed[strikeRenamedCol].div(processed[deltaAmountCol])
  );

  return processed;
}

/**
 * Merges multiple DataFrames on the "DATE" column,
 * and consolidates all "PREMIUM_GAINS" columns into a single column by summing their values.
 *
 * This avoids duplication and column name conflicts during merge operations.
 *
 * @param {dfd.DataFrame[]} dfs - Array of DataFrames to be merged.
 * @returns {dfd.DataFrame} - A single merged DataFrame with consolidated "PREMIUM_GAINS".
 */
function mergeAllDfs(dfs) {

  // Step 1: Accumulate PREMIUM_GAINS from all DataFrames using a Map keyed by DATE
  const premiumMap = new Map();

  for (const df of dfs) {
    if (!df.columns.includes(con.PREMIUM_GAINS)) continue;

    const dates = df[con.DATE].values;
    const premiums = df[con.PREMIUM_GAINS].values;

    for (let i = 0; i < dates.length; i++) {
      const date = dates[i];
      const premium = premiums[i];

      // Only accumulate if value is a number (not NaN or undefined)
      if (typeof premium === "number" && !isNaN(premium)) {
        if (premiumMap.has(date)) {
          premiumMap.set(date, premiumMap.get(date) + premium);
        } else {
          premiumMap.set(date, premium);
        }
      }
    }

    // Drop PREMIUM_GAINS from current DataFrame to avoid column conflict during merge
    df.drop({ columns: [con.PREMIUM_GAINS], inplace: true });
  }

  // Step 2: Merge all DataFrames (without PREMIUM_GAINS) on DATE using outer join
  let merged = dfs[0];
  for (let i = 1; i < dfs.length; i++) {
    merged = dfd.merge({ left: merged, right: dfs[i], on: [con.DATE], how: "outer" });
  }

  // Step 3: Create a DataFrame from the accumulated PREMIUM_GAINS map
  const premiumDf = new dfd.DataFrame({
    [con.DATE]: Array.from(premiumMap.keys()),
    [con.PREMIUM_GAINS]: Array.from(premiumMap.values()),
  });

  // Step 4: Merge the consolidated PREMIUM_GAINS into the final merged DataFrame
  merged = dfd.merge({ left: merged, right: premiumDf, on: [con.DATE], how: "outer" });
  merged = merged.fillNa(0);
  
  return merged;
}

/**
 * Calculates the final RATE column and appends it to the DataFrame.
 * This includes summing multiple amount/strike components, handling edge cases where execution coverage is 0,
 * and combining all premium and strike-based calculations into a final effective rate.
 *
 * @param {dfd.DataFrame} trans_df - The DataFrame containing all transaction fields.
 * @param {number} spot - The spot value to be used in RATE calculations.
 * @param {number} sign - A direction multiplier, usually 1 or -1.
 * @returns {dfd.DataFrame} - Updated DataFrame with new RATE and ALL_AMOUNTS columns.
 */
function calculateRate(trans_df, spot, sign) {
  // Step 1: Extract and fill all
  trans_df = trans_df.fillNa(0);

  // Step 2: Calculate execution coverage
  const executionCoverage = trans_df[con.COVERAGE_AMOUNT]

  // Step 3: Calculate allAmounts
  let allAmounts = executionCoverage.map((val, i) => {
    if (val === 0) {
      return (
        trans_df[con.FWD_COUNTER_COVERAGE_AMOUNT].values[i] +
        trans_df[con.COUNTER_BUY_CALL_DELTA_AMOUNT].values[i] +
        trans_df[con.COUNTER_SELL_CALL_DELTA_AMOUNT].values[i] +
        trans_df[con.COUNTER_BUY_PUT_DELTA_AMOUNT].values[i] +
        trans_df[con.COUNTER_SELL_PUT_DELTA_AMOUNT].values[i]
      );
    } else {
      return val;
    }
  });

  allAmounts = allAmounts.fillNa(0)
  trans_df = trans_df.addColumn(con.ALL_AMOUNTS, allAmounts);

  // Step 4: Build RATE components step by step
  const fwdRatePart = trans_df[con.FWD_COVERAGE_AMOUNT].mul(
  (trans_df[con.FWD_COVERAGE_RATE].sub(spot)).mul(sign)
  );

  const fwdCounterRatePart = trans_df[con.FWD_COUNTER_COVERAGE_AMOUNT].mul(
  (trans_df[con.FWD_COUNTER_RATE].sub(spot)).mul(sign).mul(-1)
  );

  let rate = fwdRatePart.add(fwdCounterRatePart);

  // Helper to calculate max(STRIKE - SPOT, 0) * AMOUNT * SIGN
  const addOptionComponent = (rate, amount, strike, inverse = false, minInstead = false) => {
    let diff = inverse ? strike.sub(spot).mul(-1) : strike.sub(spot);
    diff = minInstead ? diff.minimum(0) : diff.maximum(0);
    return rate.add(amount.mul(diff));
  };

  rate = addOptionComponent(rate, trans_df[con.DIRECT_CALL_DELTA_AMOUNT], trans_df[con.DIRECT_CALL_STRIKE], true);
  rate = addOptionComponent(rate, trans_df[con.COUNTER_BUY_CALL_DELTA_AMOUNT], trans_df[con.COUNTER_BUY_CALL_STRIKE], true);
  rate = addOptionComponent(rate, trans_df[con.COUNTER_SELL_CALL_DELTA_AMOUNT], trans_df[con.COUNTER_SELL_CALL_STRIKE], false, true);
  rate = addOptionComponent(rate, trans_df[con.DIRECT_PUT_DELTA_AMOUNT], trans_df[con.DIRECT_PUT_STRIKE]);
  rate = addOptionComponent(rate, trans_df[con.COUNTER_BUY_PUT_DELTA_AMOUNT], trans_df[con.COUNTER_BUY_PUT_STRIKE]);
  rate = addOptionComponent(rate, trans_df[con.COUNTER_SELL_PUT_DELTA_AMOUNT], trans_df[con.COUNTER_SELL_PUT_STRIKE], true, true);

  // Add premium gains
  rate = rate.add(trans_df[con.PREMIUM_GAINS]);

  // Final rate: divide by total amount and add spot
  rate = (rate.div(allAmounts)).mul(sign)
  rate = rate.add(spot);
  rate = rate.fillNa(0)
  trans_df = trans_df.addColumn(con.RATE, rate);

  return trans_df;
}


const mergeExposuresAndCoverages = (clientType, current_date, periodicity, term_in_months, exposures, directCoverages, counterCoverages,
                                    directCall, directPut, counterBuyCall, counterSellCall, counterBuyPut, counterSellPut, spot) =>
{

    // Define sign for option cost direction: +1 if client is exporter, -1 if client is importer
    const sign = clientType === con.EXPORTER ? 1 : -1;

    // Define the date range from current_date to the end of the term period with monthly periodicity
    const minDate = current_date;
    const maxDate = dateFun.takeDateToEndOfPeriodicity(
    dateFun.addMonths(current_date, term_in_months),
    con.PERIODICITY_MONTH
    );

    // Generate a complete list of dates between minDate and maxDate based on the specified periodicity (e.g., monthly)
    const allDates = dateFun.getDatesBetween(minDate, maxDate, periodicity);

    // Exposures
    // -------------------
    // Prepare an array of exposures with renamed keys: 'date' and 'exposure_amount'
    const exposureData = exposures.map(exp => ({
    [con.DATE]: exp.expiration_date,
    [con.EXPOSURE_AMOUNT]: exp.amount
    }));                       
    // Add rows for all possible dates with zero exposure amount to fill missing dates
    const exposureWithAllDates = exposureData.concat(
    allDates.map(d => ({ [con.DATE]: dateFun.formatDate(d), [con.EXPOSURE_AMOUNT]: 0 }))
    );
    // Convert the combined array to a DataFrame for easier manipulation with danfo.js
    let exposuresDf = new dfd.DataFrame(exposureWithAllDates);

    // Group by 'date' and sum exposure amounts to consolidate duplicates
    exposuresDf = exposuresDf
    .groupby([con.DATE])
    .sum()
    .rename({ [`${con.EXPOSURE_AMOUNT}_sum`]: con.EXPOSURE_AMOUNT });

    // Direct FWD Coverages
    // -------------------
    directCoverages = processFWDCoverages(
        directCoverages,
        con.FWD_COVERAGE_AMOUNT,
        con.FWD_COVERAGE_RATE
    );

    // Counter FWD Coverages
    // -------------------
    counterCoverages = processFWDCoverages(
        counterCoverages,
        con.FWD_COUNTER_COVERAGE_AMOUNT,
        con.FWD_COUNTER_RATE
    );

    // Direct Call Coverages
    // -------------------
    directCall = processOptionsCoverages(directCall, {
        deltaAmountCol: con.DIRECT_CALL_DELTA_AMOUNT,
        amountRenamedCol: con.DIRECT_CALL_AMOUNT,
        strikeRenamedCol: con.DIRECT_CALL_STRIKE,
    }, sign);

    // Counter Buy Call Coverages
    // -------------------
    counterBuyCall = processOptionsCoverages(counterBuyCall, {
        deltaAmountCol: con.COUNTER_BUY_CALL_DELTA_AMOUNT,
        amountRenamedCol: con.COUNTER_BUY_CALL_AMOUNT,
        strikeRenamedCol: con.COUNTER_BUY_CALL_STRIKE,
    }, sign);

    // Counter Sell Call Coverages
    // -------------------
    counterSellCall = processOptionsCoverages(counterSellCall, {
        deltaAmountCol: con.COUNTER_SELL_CALL_DELTA_AMOUNT,
        amountRenamedCol: con.COUNTER_SELL_CALL_AMOUNT,
        strikeRenamedCol: con.COUNTER_SELL_CALL_STRIKE,
    }, sign);
  
    // Direct Put Coverages
    // -------------------
    directPut = processOptionsCoverages(directPut, {
        deltaAmountCol: con.DIRECT_PUT_DELTA_AMOUNT,
        amountRenamedCol: con.DIRECT_PUT_AMOUNT,
        strikeRenamedCol: con.DIRECT_PUT_STRIKE,
    }, sign);
  
    // Counter Buy Put Coverages
    // -------------------
    counterBuyPut = processOptionsCoverages(counterBuyPut, {
        deltaAmountCol: con.COUNTER_BUY_PUT_DELTA_AMOUNT,
        amountRenamedCol: con.COUNTER_BUY_PUT_AMOUNT,
        strikeRenamedCol: con.COUNTER_BUY_PUT_STRIKE,
    }, sign);

    // Counter Sell Put Coverages    
    // -------------------
    counterSellPut = processOptionsCoverages(counterSellPut, {
        deltaAmountCol: con.COUNTER_SELL_PUT_DELTA_AMOUNT,
        amountRenamedCol: con.COUNTER_SELL_PUT_AMOUNT,
        strikeRenamedCol: con.COUNTER_SELL_PUT_STRIKE,
    }, sign);

    // Merges all jsons into a single DataFrame
    let trans_df = mergeAllDfs([exposuresDf, directCoverages, counterCoverages, directCall, counterBuyCall, counterSellCall, directPut, counterBuyPut, counterSellPut])

    // Consolidate Coverages
    // -------------------
    // Option Coverage Amount
    trans_df = trans_df.addColumn(con.OPTION_COVERAGE_AMOUNT, trans_df[con.DIRECT_CALL_DELTA_AMOUNT].add(trans_df[con.DIRECT_PUT_DELTA_AMOUNT]))
    
    // Option Rate (One of them must be 0, so I can just sum)
    trans_df = trans_df.addColumn(con.OPTION_COVERAGE_RATE, trans_df[con.DIRECT_CALL_STRIKE].add(trans_df[con.DIRECT_PUT_STRIKE]))

    // Total Coverage Amount
    trans_df = trans_df.addColumn(con.COVERAGE_AMOUNT, trans_df[con.OPTION_COVERAGE_AMOUNT].add(trans_df[con.FWD_COVERAGE_AMOUNT]))

    // Calculate RATE
    trans_df = calculateRate(trans_df, spot, sign)
    
    // Adds Counter coverages amount
    let counter_cov_amount = trans_df[con.FWD_COUNTER_COVERAGE_AMOUNT].add(trans_df[con.COUNTER_BUY_CALL_DELTA_AMOUNT]).add(trans_df[con.COUNTER_BUY_PUT_DELTA_AMOUNT]).add(trans_df[con.COUNTER_SELL_CALL_DELTA_AMOUNT]).add(trans_df[con.COUNTER_SELL_PUT_DELTA_AMOUNT])
    trans_df = trans_df.addColumn(con.COUNTER_COVERAGE_AMOUNT, counter_cov_amount)
    trans_df = trans_df.addColumn(con.REAL_EXPOSURE, trans_df[con.EXPOSURE_AMOUNT])

    // Sorts
    trans_df = sortByDate(trans_df, con.DATE);

    return(trans_df)

}

/**
 * Removes exposure amounts from a transaction DataFrame based on available account balances.
 * The removal is done chronologically: starting from the first row, the function subtracts 
 * as much as possible from each `EXPOSURE_AMOUNT` until the total available `amount` is exhausted.
 *
 * @param {dfd.DataFrame} trans_df - DataFrame of transactions containing the EXPOSURE_AMOUNT column.
 * @param {Object} accounts - An object where each value has the shape {account_type, amount}.
 * @returns {dfd.DataFrame} - Updated DataFrame with EXPOSURE_AMOUNT adjusted.
 */
const removeExposures = (trans_df, accounts) => {
  // Convert the accounts object into a DataFrame with only account_type and amount
  const accountData = Object.values(accounts).map(row => ({
    account_type: row.account_type,
    amount: row.amount
  }));

  const accountDf = new dfd.DataFrame(accountData);

  // Total amount available from all accounts
  let totalAvailable = accountDf[con.AMOUNT].sum();

  // Copy of exposure amounts to avoid mutating the original
  const updatedExposures = trans_df[con.EXPOSURE_AMOUNT].apply((exposure) => {
    // Determine how much we can subtract (max available or the current exposure)
    const toRemove = Math.min(exposure, totalAvailable);

    // Update remaining balance
    totalAvailable -= toRemove;

    // Return the adjusted exposure
    return exposure - toRemove;
  });

  // Update the DataFrame with the new exposure values
  trans_df = setColumn(trans_df, con.EXPOSURE_AMOUNT, updatedExposures);
  
  // Adds net Exposure
  let net_exposure_amount = trans_df[con.EXPOSURE_AMOUNT].sub(trans_df[con.COVERAGE_AMOUNT]).add(trans_df[con.COUNTER_COVERAGE_AMOUNT])
  trans_df = trans_df.addColumn(con.NET_EXPOSURE_AMOUNT, net_exposure_amount)
  return trans_df;
};



/**
 * Attaches a dollar scenario to the transaction DataFrame by generating projected
 * dollar values based on the number of days between each transaction date and the current date.
 *
 * This function adds two columns to the DataFrame:
 * - VAR_RATE: The simulated dollar rate per transaction.
 * - MAX_VAR_RATE: Currently identical to VAR_RATE, intended for further max rate analysis.
 *
 * @param {string | Date} current_date - The reference date for calculating the number of days.
 * @param {number} spot - The current spot price of the dollar.
 * @param {dfd.DataFrame} trans_df - The transaction DataFrame, assumed to be sorted by date.
 * @param {Function} scenarioGenerationFun - A function that receives (spot, days) and returns the projected dollar value.
 * @returns {dfd.DataFrame} - The updated DataFrame with VAR_RATE and MAX_VAR_RATE columns.
 */
const attachDollarScenario = (current_date, spot, trans_df, scenarioGenerationFun) => {
  // Step 1: Calculate number of days between each transaction date and current_date
  const amountDays = trans_df[con.DATE].map(d =>
    dateFun.daysBetweenDates(current_date, d)
  );

  // Step 2: Generate dollar values for each number of days using the scenario generator
  const dollarValues = amountDays.map(days => scenarioGenerationFun(spot, days));

  // Step 3: Add the projected dollar value as VAR_RATE
  trans_df = trans_df.addColumn(con.VAR_RATE, dollarValues);

  // Step 4: Duplicate VAR_RATE into MAX_VAR_RATE (useful for future max calculations)
  trans_df = trans_df.addColumn(con.MAX_VAR_RATE, dollarValues);

  return trans_df;
};


/**
 * Adds execution flags and settlement rates to a transaction DataFrame based on VAR scenarios for options.
 *
 * This function evaluates if each option (calls/puts) is exercised under the simulated VAR rate,
 * and assigns both execution amounts and settlement rates accordingly.
 *
 * @param {dfd.DataFrame} trans_df - Transaction DataFrame with VAR_RATE and option strike/amount columns.
 * @returns {dfd.DataFrame} - Updated DataFrame with execution amounts and settlement rates for each option leg.
 */
const optionVARConsolidation = (trans_df) => {

    // --- Internal helpers ---

    // Returns min or max between VAR_RATE and strike
    const getSettlementRate = (columns, row, strikeCol, mode) => {
        const varRate = row[columns.indexOf(con.VAR_RATE)];
        const strike = row[columns.indexOf(strikeCol)];
        return mode === "min" ? Math.min(varRate, strike) : Math.max(varRate, strike);
    };

    // Determines if option is executed and returns amount or 0
    const getExecutionAmount = (columns, row, strikeCol, amountCol, optionType) => {
        const varRate = row[columns.indexOf(con.VAR_RATE)];
        const strike = row[columns.indexOf(strikeCol)];
        const amount = row[columns.indexOf(amountCol)];

        if (optionType === con.CALL) {
            return varRate > strike ? amount : 0;
        } else {
            return varRate < strike ? amount : 0;
        }
    };

    // --- Map of all option legs and config ---
    const optionConfig = [
        { strike: con.DIRECT_CALL_STRIKE, amount: con.DIRECT_CALL_AMOUNT, type: con.CALL, exec: con.EXECUTION_DIRECT_CALL_VAR, rate: con.DIRECT_CALL_SETTLEMENT_RATE, mode: 'min' },
        { strike: con.COUNTER_BUY_CALL_STRIKE, amount: con.COUNTER_BUY_CALL_AMOUNT, type: con.CALL, exec: con.EXECUTION_COUNTER_BUY_CALL_VAR, rate: con.COUNTER_BUY_CALL_SETTLEMENT_RATE, mode: 'min' },
        { strike: con.COUNTER_SELL_CALL_STRIKE, amount: con.COUNTER_SELL_CALL_AMOUNT, type: con.CALL, exec: con.EXECUTION_COUNTER_SELL_CALL_VAR, rate: con.COUNTER_SELL_CALL_SETTLEMENT_RATE, mode: 'min' },
        { strike: con.DIRECT_PUT_STRIKE, amount: con.DIRECT_PUT_AMOUNT, type: con.PUT, exec: con.EXECUTION_DIRECT_PUT_VAR, rate: con.DIRECT_PUT_SETTLEMENT_RATE, mode: 'max' },
        { strike: con.COUNTER_BUY_PUT_STRIKE, amount: con.COUNTER_BUY_PUT_AMOUNT, type: con.PUT, exec: con.EXECUTION_COUNTER_BUY_PUT_VAR, rate: con.COUNTER_BUY_PUT_SETTLEMENT_RATE, mode: 'max' },
        { strike: con.COUNTER_SELL_PUT_STRIKE, amount: con.COUNTER_SELL_PUT_AMOUNT, type: con.PUT, exec: con.EXECUTION_COUNTER_SELL_PUT_VAR, rate: con.COUNTER_SELL_PUT_SETTLEMENT_RATE, mode: 'max' },
    ];

    // --- Apply calculations ---
    optionConfig.forEach(opt => {
        const execution = trans_df.apply(row =>
            getExecutionAmount(trans_df.columns, row, opt.strike, opt.amount, opt.type), { axis: 1 }
        );

        const settlement = trans_df.apply(row =>
            getSettlementRate(trans_df.columns, row, opt.strike, opt.mode), { axis: 1 }
        );

        trans_df = trans_df.addColumn(opt.exec, execution);
        trans_df = trans_df.addColumn(opt.rate, settlement);
    });

    // --- Fill NA with 0 ---
    const columnsToFill = optionConfig.flatMap(opt => [opt.exec, opt.rate]);
    const zeros = Array(columnsToFill.length).fill(0);
    trans_df = trans_df.fillNa(zeros, { columns: columnsToFill });

    return trans_df;
};


/**
 * Adjusts the dates in a DataFrame to the end of their corresponding period (e.g., end of month or week).
 * If periodicity is 'DAY', the original DataFrame is returned unchanged.
 * 
 * @param {DataFrame} trans_df - The input DataFrame with a date column.
 * @param {string} periodicity - The periodicity to adjust to ('DAY', 'WEEK', 'MONTH').
 * @returns {DataFrame} - A new DataFrame with the adjusted date column.
 */
const adjustPeriodicity = (trans_df, periodicity) => {
    if (periodicity === con.PERIODICITY_DAY) return trans_df;

    // Adjust each date to the end of its corresponding period
    const adjusted_dates = trans_df[con.DATE].map((d) =>
        dateFun.formatDate(dateFun.takeDateToEndOfPeriodicity(d, periodicity))
    );

    // Return DataFrame with updated date column
    return setColumn(trans_df, con.DATE, adjusted_dates);
};


/**
 * Groups the transaction DataFrame by date, applying weighted averages, sums, and max operations.
 * 
 * Steps:
 * 1. Identify columns for weighted averages and their weights.
 * 2. Identify columns for max aggregation.
 * 3. Calculate total sums of weight columns grouped by date.
 * 4. Calculate max values for max columns grouped by date.
 * 5. Multiply columns to average by their weights to prepare weighted sums.
 * 6. Sum all columns grouped by date.
 * 7. Rename columns to remove '_sum' suffix.
 * 8. Compute final weighted averages dividing weighted sums by total weights.
 * 9. Insert max columns back into the result.
 * 10. Calculate special coverage percentages if needed.
 * 11. Reset index and fill NaNs with zeros.
 * 
 * @param {DataFrame} trans_df - Input dataframe with transaction data.
 * @returns {DataFrame} DataFrame grouped by date with aggregated values.
 */
const groupByDate = (trans_df) => {

    // Step 0: Cache original columns for reuse
    const cols = trans_df.columns;

    // Step 1: Define weighted average columns and their weight columns
    const colsToAvg = {
        [con.VAR_RATE] : con.NET_EXPOSURE_AMOUNT,
        [con.RATE] : con.COVERAGE_AMOUNT,
        [con.FWD_COVERAGE_RATE] : con.FWD_COVERAGE_AMOUNT,
        [con.FWD_COUNTER_RATE] : con.FWD_COUNTER_COVERAGE_AMOUNT,
        [con.OPTION_COVERAGE_RATE] : con.OPTION_COVERAGE_AMOUNT,
        [con.DIRECT_CALL_SETTLEMENT_RATE] : con.DIRECT_CALL_AMOUNT,
        [con.COUNTER_BUY_CALL_SETTLEMENT_RATE] : con.COUNTER_BUY_CALL_AMOUNT,
        [con.COUNTER_SELL_CALL_SETTLEMENT_RATE] : con.COUNTER_SELL_CALL_AMOUNT,
        [con.DIRECT_PUT_SETTLEMENT_RATE] : con.DIRECT_PUT_AMOUNT,
        [con.COUNTER_BUY_PUT_SETTLEMENT_RATE] : con.COUNTER_BUY_PUT_AMOUNT,
        [con.COUNTER_SELL_PUT_SETTLEMENT_RATE] : con.COUNTER_SELL_PUT_AMOUNT,
        [con.EFFECTIVE_RATE] : con.NET_EXPOSURE_AMOUNT,
        [con.VAR_X_USD] : con.NET_EXPOSURE_AMOUNT,
        [`${con.LOWER_BAND}_${con.VAR_X_USD}`] : con.NET_EXPOSURE_AMOUNT,
        [`${con.MID_BAND}_${con.VAR_X_USD}`] : con.NET_EXPOSURE_AMOUNT,
        [`${con.HIGHER_BAND}_${con.VAR_X_USD}`] : con.NET_EXPOSURE_AMOUNT
    };

    // Step 2: Define columns that should be aggregated using max
    const colsToMax = [
        con.MAX_VAR_RATE,
        `${con.LOWER_BAND}_${con.PERCENTAGES}`,
        `${con.MID_BAND}_${con.PERCENTAGES}`,
        `${con.HIGHER_BAND}_${con.PERCENTAGES}`
    ];

    // Step 3: Calculate total sums of all weight columns per date
    const weightColsSet = new Set(Object.values(colsToAvg));
    const weightCols = cols.filter(col => weightColsSet.has(col));
    const totalsCols = [...weightCols, con.DATE];
    const totals = trans_df.loc({ columns: totalsCols })
                          .groupby([con.DATE])
                          .sum()
                          .setIndex({ column: con.DATE });

    // Step 4: Calculate max values of max columns per date
    const maxCols = cols.filter(col => colsToMax.includes(col));
    const maxGroupCols = [...maxCols, con.DATE];
    const maxs = trans_df.loc({ columns: maxGroupCols })
                        .groupby([con.DATE])
                        .max()
                        .setIndex({ column: con.DATE });

    // Step 5: Multiply weighted average columns by their weight columns (prepare weighted sums)
    for (const [colAvg, colWeight] of Object.entries(colsToAvg)) {
        if (cols.includes(colAvg)) {
            const weightedCol = trans_df[colAvg].mul(trans_df[colWeight]);
            trans_df = setColumn(trans_df, colAvg, weightedCol);
        }
    }

    // Step 6: Sum all columns grouped by date
    trans_df = trans_df.groupby([con.DATE])
                       .sum()
                       .setIndex({ column: con.DATE });

    // Step 7: Rename columns to remove '_sum' suffix added by aggregation
    const renameMap = {};
    cols.forEach(col => {
        renameMap[`${col}_sum`] = col;
    });
    trans_df = trans_df.rename(renameMap);

    // Step 8: Compute final weighted averages dividing weighted sums by totals of weights
    for (const [colAvg, colWeight] of Object.entries(colsToAvg)) {
        if (cols.includes(colAvg)) {
            const divisor = totals[`${colWeight}_sum`];

            // Special case for "VAR_RATE". If divisor is 0 or NaN, use MAX_VAR_RATE
            if (colAvg === con.VAR_RATE && (!divisor || isNaN(divisor))) {
                const fallback = maxs[`${con.MAX_VAR_RATE}_max`];
                trans_df = setColumn(trans_df, colAvg, fallback);
            } else {
                const weightedAvg = trans_df[colAvg].div(divisor);
                trans_df = setColumn(trans_df, colAvg, weightedAvg);
            }
        }
    }

    // Step 9: Insert max columns back into the DataFrame
    maxCols.forEach(col => {
        if (cols.includes(col)) {
            trans_df = setColumn(trans_df, col, maxs[`${col}_max`]);
        }
    });

    // Step 10: Compute special coverage percentage columns if present
    if (cols.includes(con.COVERAGE_PERCENTAGE)) {
        const coveragePercentage = computeDirectCoverageAverage(trans_df);
        trans_df = setColumn(trans_df, con.COVERAGE_PERCENTAGE, coveragePercentage);
        trans_df = trans_df.fillNa([0], { columns: [con.COVERAGE_PERCENTAGE] });
    }
    if (cols.includes(con.COUNTER_COVERAGE_PERCENTAGE)) {
        const counterCoveragePercentage = computeCounterCoverageAverage(trans_df);
        trans_df = setColumn(trans_df, con.COUNTER_COVERAGE_PERCENTAGE, counterCoveragePercentage);
        trans_df = trans_df.fillNa([0], { columns: [con.COUNTER_COVERAGE_PERCENTAGE] });
    }

    // Step 11: Reset index to bring date back as a regular column and fill NaNs with zeros
    trans_df = trans_df.resetIndex();
    trans_df = trans_df.fillNa(Array(trans_df.columns.length).fill(0), { columns: trans_df.columns });

    return trans_df;
};


/**
 * Applies an over coverage scheme to a transaction DataFrame sorted by date.
 * 
 * The function simulates rolling coverage: excess coverage from one date
 * is carried forward to the next dates and used to fulfill exposure needs.
 * Weighted rates are calculated accordingly.
 * 
 * @param {DataFrame} trans_df - The transaction DataFrame, sorted by date.
 * @returns {DataFrame} - The DataFrame with updated COVERAGE_AMOUNT and RATE columns.
 */
const applyOverCoverageScheme = (trans_df) => {
    // --- NOTE: Input DataFrame must be sorted by date for correct results ---

    // Tracks remaining over-coverage and its associated rate
    let over_coverage = 0;
    let over_rate = 0;

    // Arrays to store updated values per row
    const new_coverages = [];
    const new_rates = [];

    // Iterate through each row by index (assumes unique index per date)
    trans_df.index.forEach((i, position) => {
        // Extract the row as a plain object
        const row = {};
        trans_df.columns.forEach(col => {
            row[col] = trans_df[col].values[position];
        });

        // Extract relevant values safely
        let amount_to_cover = row[con.NET_EXPOSURE_AMOUNT] ?? 0;
        const coverage = row[con.COVERAGE_AMOUNT] ?? 0;
        const rate = row[con.RATE] ?? 0;

        // Initialize new values
        let new_coverage = 0;
        let new_rate = 0;

        // --- Phase 1: Use over coverage first ---
        const used_from_over = Math.min(amount_to_cover, over_coverage);
        new_coverage += used_from_over;
        new_rate = amount_to_cover > 0 ? over_rate : 0;

        // Update tracking
        amount_to_cover -= used_from_over;
        over_coverage -= used_from_over;

        // --- Phase 2: Add current coverage to the over coverage pool ---
        if (coverage > 0) {
            const total_weight = over_coverage + coverage;
            over_rate = total_weight > 0
                ? ((over_rate * over_coverage) + (rate * coverage)) / total_weight
                : 0;
            over_coverage += coverage;
        }

        // --- Phase 3: Apply remaining coverage if needed ---
        const remaining = Math.min(amount_to_cover, over_coverage);
        new_coverage += remaining;
        new_rate = remaining > 0 ? over_rate : new_rate;

        over_coverage -= remaining;

        // If no coverage left, reset rate
        if (over_coverage === 0) {
            over_rate = 0;
        }

        // --- Store results for this row ---
        new_coverages.push(new_coverage);
        new_rates.push(new_rate);
    });

    // --- Final adjustment if unused over coverage remains ---
    if (over_coverage > 0 && trans_df.index.length > 0) {
        const last_index = trans_df.index[trans_df.index.length - 1];
        const last_coverage = new_coverages[last_index];
        const last_rate = new_rates[last_index];

        const total_weight = last_coverage + over_coverage;
        const adjusted_rate = total_weight > 0
            ? ((last_rate * last_coverage) + (over_rate * over_coverage)) / total_weight
            : last_rate;

        new_coverages[last_index] += over_coverage;
        new_rates[last_index] = adjusted_rate;
    }

    // --- Update the DataFrame with new values ---
    trans_df = setColumn(trans_df, con.COVERAGE_AMOUNT, new_coverages);
    trans_df = setColumn(trans_df, con.RATE, new_rates);

    return trans_df;
};


// LEGACY
// const attachRealPosition = (trans_df) =>
// {    

//     let exp = trans_df[con.EXPOSURE_AMOUNT]
//     let cov = trans_df[con.COVERAGE_AMOUNT]

//     // Sets the exposure amount unless its 0, in that case it will be de coverage
//     let real_position = exp
//     real_position = real_position.add(cov.mul(exp.eq(0)))

//     trans_df = trans_df.addColumn(con.REAL_POSITION, real_position)

//     return(trans_df)

// }



/**
 * Computes opportunistic VAR-related metrics for a transaction DataFrame.
 * Adds columns for direct/counter deltas, adjusted VAR rate, coverage percentage,
 * effective rate, VAR per USD, and final VAR based on exposures and executed instruments.
 * 
 * @param {DataFrame} trans_df - Input transaction DataFrame (danfo.js).
 * @param {number} spot - Current spot rate.
 * @param {string} client_type - Type of client ('IMPORTER' or 'EXPORTER').
 * @param {number} passthrough - Pass-through benefit ratio (0-1).
 * @returns {DataFrame} Updated DataFrame with new VAR-related columns.
 */
const addOpportunisticVar = (trans_df, spot, client_type, passthrough) => {
    // Retrieve all necessary columns from the input DataFrame
    let exp = trans_df[con.EXPOSURE_AMOUNT];
    let var_rate = trans_df[con.VAR_RATE];
    let max_var_rate = trans_df[con.MAX_VAR_RATE];

    let fwd_cov_amount = trans_df[con.FWD_COVERAGE_AMOUNT];
    let fwd_cov_rate = trans_df[con.FWD_COVERAGE_RATE];
    let counter_fwd_amount = trans_df[con.FWD_COUNTER_COVERAGE_AMOUNT];
    let counter_fwd_rate = trans_df[con.FWD_COUNTER_RATE];

    let call_cov_amount = trans_df[con.EXECUTION_DIRECT_CALL_VAR];
    let put_cov_amount = trans_df[con.EXECUTION_DIRECT_PUT_VAR];
    let call_cov_rate = trans_df[con.DIRECT_CALL_SETTLEMENT_RATE];
    let put_cov_rate = trans_df[con.DIRECT_PUT_SETTLEMENT_RATE];

    let buy_call_counter_amount = trans_df[con.EXECUTION_COUNTER_BUY_CALL_VAR];
    let buy_put_counter_amount = trans_df[con.EXECUTION_COUNTER_BUY_PUT_VAR];
    let buy_call_counter_rate = trans_df[con.COUNTER_BUY_CALL_SETTLEMENT_RATE];
    let buy_put_counter_rate = trans_df[con.COUNTER_BUY_PUT_SETTLEMENT_RATE];

    let sell_call_counter_amount = trans_df[con.EXECUTION_COUNTER_SELL_CALL_VAR];
    let sell_put_counter_amount = trans_df[con.EXECUTION_COUNTER_SELL_PUT_VAR];
    let sell_call_counter_rate = trans_df[con.COUNTER_SELL_CALL_SETTLEMENT_RATE];
    let sell_put_counter_rate = trans_df[con.COUNTER_SELL_PUT_SETTLEMENT_RATE];

    let coverage_amount = trans_df[con.COVERAGE_AMOUNT];
    let counter_coverage_amount = trans_df[con.COUNTER_COVERAGE_AMOUNT];
    let gl_premiums = trans_df[con.PREMIUM_GAINS];

    let counter_buy_call_total_amount = trans_df[con.COUNTER_BUY_CALL_AMOUNT];
    let counter_sell_call_total_amount = trans_df[con.COUNTER_SELL_CALL_AMOUNT];
    let counter_buy_put_total_amount = trans_df[con.COUNTER_BUY_PUT_AMOUNT];
    let counter_sell_put_total_amount = trans_df[con.COUNTER_SELL_PUT_AMOUNT];

    // === DELTA CALCULATIONS ===

    // Compute direct delta = (option coverage) / (call + put amount), only if total > 0
    let op_cov_amount = trans_df[con.OPTION_COVERAGE_AMOUNT];
    let direct_call_amount = trans_df[con.DIRECT_CALL_AMOUNT];
    let direct_put_amount = trans_df[con.DIRECT_PUT_AMOUNT];
    let total_direct_amount = direct_call_amount.add(direct_put_amount);
    let direct_delta = op_cov_amount.div(total_direct_amount.add(epsylon));
    direct_delta = direct_delta.mul(total_direct_amount.gt(0));
    trans_df = trans_df.addColumn(con.DIRECT_DELTA, direct_delta);

    // Compute counter delta based on total counter options vs. amount
    let op_counter_cov_amount = trans_df[con.COUNTER_BUY_CALL_DELTA_AMOUNT]
        .add(trans_df[con.COUNTER_SELL_CALL_DELTA_AMOUNT])
        .add(trans_df[con.COUNTER_BUY_PUT_DELTA_AMOUNT])
        .add(trans_df[con.COUNTER_SELL_PUT_DELTA_AMOUNT]);

    let total_counter_amount = counter_buy_call_total_amount
        .add(counter_sell_call_total_amount)
        .add(counter_buy_put_total_amount)
        .add(counter_sell_put_total_amount);

    let counter_delta = op_counter_cov_amount.div(total_counter_amount.add(epsylon));
    counter_delta = counter_delta.mul(total_counter_amount.gt(0));
    trans_df = trans_df.addColumn(con.COUNTER_DELTA, counter_delta);

    // === VAR RATE ADJUSTMENT ===

    // Set VAR rate to max if no exposure or coverage exists
    let use_var_rate = exp.gt(0).or(counter_coverage_amount.gt(0)).or(coverage_amount.gt(0));
    let use_max_rate = exp.eq(0).and(counter_coverage_amount.eq(0)).and(coverage_amount.eq(0));
    var_rate = var_rate.mul(use_var_rate).add(max_var_rate.mul(use_max_rate));
    trans_df = setColumn(trans_df, con.VAR_RATE, var_rate);

    // === COVERAGE PERCENTAGE ===
    let coverage_percentage = computeDirectCoverageAverage(trans_df);
    trans_df = trans_df.addColumn(con.COVERAGE_PERCENTAGE, coverage_percentage);
    trans_df = trans_df.fillNa([0], { columns: [con.COVERAGE_PERCENTAGE] });

    // === PASSTHROUGH BENEFIT ===
    let sign = client_type === con.IMPORTER ? 1 : -1;
    let benefit = 0;
    if (client_type === con.IMPORTER) {
        benefit = var_rate.sub(spot).mul(passthrough);
        benefit = benefit.mul(benefit.gt(0));  // Only positive benefit allowed
    }

    // === EFFECTIVE RATE CALCULATION ===

    // Execution coverage determines whether to consider counter side only or both sides
    let execution_coverage = fwd_cov_amount.add(call_cov_amount).add(put_cov_amount);
    let all_amounts = execution_coverage.map((val, i) => {
        return val === 0
            ? counter_fwd_amount.values[i] + counter_buy_call_total_amount.values[i] +
              counter_sell_call_total_amount.values[i] + counter_buy_put_total_amount.values[i] +
              counter_sell_put_total_amount.values[i]
            : fwd_cov_amount.values[i] + call_cov_amount.values[i] + put_cov_amount.values[i] +
              counter_fwd_amount.values[i] + buy_call_counter_amount.values[i] +
              sell_call_counter_amount.values[i] + buy_put_counter_amount.values[i] +
              sell_put_counter_amount.values[i];
    });

    let effective_rate = exp.mul(var_rate);
    effective_rate = effective_rate
        .sub(fwd_cov_amount.mul(var_rate.sub(fwd_cov_rate)).mul(sign))
        .sub(call_cov_amount.mul(var_rate.sub(call_cov_rate)))
        .sub(put_cov_amount.mul(put_cov_rate.sub(var_rate)))
        .sub(counter_fwd_amount.mul(counter_fwd_rate.sub(var_rate)).mul(sign))
        .sub(buy_call_counter_amount.mul(var_rate.sub(buy_call_counter_rate)))
        .sub(sell_call_counter_amount.mul(sell_call_counter_rate.sub(var_rate)))
        .sub(buy_put_counter_amount.mul(buy_put_counter_rate.sub(var_rate)))
        .sub(sell_put_counter_amount.mul(var_rate.sub(sell_put_counter_rate)))
        .add(gl_premiums)
        .sub(benefit);

    let effective_rate_cov = effective_rate.div(all_amounts.add(epsylon));
    effective_rate = effective_rate.div(exp.add(epsylon));

    // === VAR PER USD ===

    let var_x_usd = effective_rate.sub(spot);
    var_x_usd = var_x_usd.mul(exp.gt(0));
    var_x_usd = var_x_usd.add(effective_rate_cov.mul(exp.eq(0)));

    trans_df = trans_df.addColumn(con.VAR_X_USD, var_x_usd);
    trans_df = trans_df.fillNa([0], { columns: [con.VAR_X_USD] });

    // === FINAL EFFECTIVE RATE ===
    effective_rate = effective_rate.mul(exp.gt(0)).add(var_x_usd.add(var_rate).mul(exp.eq(0)));
    trans_df = trans_df.addColumn(con.EFFECTIVE_RATE, effective_rate);
    trans_df = trans_df.fillNa([0], { columns: [con.EFFECTIVE_RATE] });

    // === FINAL VAR ===
    let opportunistic_var = var_x_usd.mul(exp);
    opportunistic_var = opportunistic_var.add(var_x_usd.mul(all_amounts).mul(exp.eq(0)));
    trans_df = trans_df.addColumn(con.VAR, opportunistic_var);
    trans_df = trans_df.fillNa([0], { columns: [con.VAR] });

    return trans_df;
};



const addVarByBands = (current_date, trans_df, spot, client_type, average_for_percentage, monthly_bands, bands_with) =>
{

    // Term in months
    let num_periods = trans_df.shape[0];

    // Gets month periods
    let month_periods = trans_df[con.DATE].apply((d) => dateFun.monthsBetweenDates(current_date, d)).values

    // Creates the percentage vector
    let terms = [...Array(num_periods).keys()].map(val => val === 0 ? 1 : val) // Replaces 0 with 1 to avoid Nan

    // Extracts the transactions    
    let all_amounts = trans_df[con.ALL_AMOUNTS].tensor
    let exposures = trans_df[con.EXPOSURE_AMOUNT].tensor
    let counter = trans_df[con.COUNTER_COVERAGE_AMOUNT].tensor
    let effective_rate = trans_df[con.EFFECTIVE_RATE].tensor
    let coverages = trans_df[con.COVERAGE_AMOUNT].tensor
    let coverage_percentage = trans_df[con.COVERAGE_PERCENTAGE].tensor
    let total_expo = exposures.add(counter)
    // Sets Max Coverage to 1
    coverage_percentage = coverage_percentage.mul(coverage_percentage.less(1)).add(coverage_percentage.greaterEqual(1).mul(1))
    
    let var_rate = trans_df[con.VAR_RATE].tensor
    let max_var_rate = trans_df[con.MAX_VAR_RATE].tensor

    

    // Iterates over each Band
    con.BANDS.forEach((band_type) =>{

        let sign =  client_type === con.IMPORTER ? 1 : -1
        
        // Extracts the band function 
        let bands_fun = getBandsConstructionFunction(monthly_bands, bands_with, band_type);

        let band_values = tf.tensor(month_periods.map((i) => bands_fun(i)))        
        let percentages = band_values.sub(coverage_percentage).mul(average_for_percentage).div(terms)

        
        // Sets negatives to zero
        percentages = tf.maximum(percentages, 0)
       

        // Easy way to set first to zero
        percentages = percentages.dataSync()
        percentages[0] = 0
        percentages = tf.tensor(percentages)


        // Creates Matrix
        // Matrix where if column >= row the the value is zero
        // NOTE: There is probably a better way to do this using Danfo.js or TensorFlow.js but for the life of me
        // I could not find it. I looked for a way to update values based on slicing in Danfos, but the documentation is still pretty
        // raw and not a lot of resouces online for it

        let percentage_array = percentages.dataSync();
        let matrix = tf.stack([...Array(num_periods).keys()].map((i) => percentage_array.map((_, j) => j >= i ? 0 : percentage_array[i])))        
        // Multiplies by exposures plus counter coverages
        matrix = matrix.mul(tf.reshape(total_expo,[num_periods,1]))
        
        // Sums
        let sums = matrix.sum(1)
   

        // Extracts the effective rate by band
        // Computes totals 
        let percentage_amount = matrix.mul(max_var_rate).sum(1)
        percentage_amount = percentage_amount.sub(sums.mul(var_rate)).mul(sign).mul(-1)     
        

        // Constructs rate 
        // let efective_band_rate = covered_amount.add(percentage_amount).add(oportunistic_amount).div(exposures.add(epsylon))
        let efective_band_rate = effective_rate.mul(exposures)
        efective_band_rate = efective_band_rate.add(percentage_amount);
        let effective_rate_cov = efective_band_rate.div(all_amounts.add(epsylon))
        efective_band_rate = efective_band_rate.div(exposures.add(epsylon))

        // Var by USD
        let var_x_usd =  efective_band_rate.sub(spot).mul(exposures.greater(0))
        var_x_usd = var_x_usd.add(effective_rate_cov.mul(exposures.equal(0)))

    
        // If net_exposure_amount is <= 0, will assign the efective rate
        efective_band_rate = efective_band_rate.mul(exposures.greater(0)).add(var_x_usd.add(spot).mul(exposures.equal(0)))

        // Band Var
        let band_var =  var_x_usd.mul(exposures).mul(exposures.greater(0))
        band_var = band_var.add(var_x_usd.mul(all_amounts).mul(exposures.equal(0)))

        // Adds percentages
        trans_df = trans_df.addColumn(`${band_type}_${con.PERCENTAGES}`, new dfd.Series(band_values))

        // Adds the percentage to reach
        // let percentage_to_reach = tf.maximum(0, band_values.mul(net_exposure_amount).sub(coverages))
        // trans_df = trans_df.addColumn(`${band_type}_${con.PERCENTAGE_TO_REACH}`, new dfd.Series(percentage_to_reach))


        // Adds amount to reach the band
        let amount_to_reach = tf.maximum(0, band_values.mul(exposures).add(counter).sub(coverages))
        trans_df = trans_df.addColumn(`${band_type}_${con.AMOUNT_TO_REACH}`, new dfd.Series(amount_to_reach))

        // Adds the Band Var
        trans_df = trans_df.addColumn(`${band_type}_${con.VAR}`, new dfd.Series(band_var))

        // Adds the Var by USD
        trans_df = trans_df.addColumn(`${band_type}_${con.VAR_X_USD}`, new dfd.Series(var_x_usd))


    })

    return(trans_df)

}




const attachPositionInBands = (trans_df) =>
{

    
    // Calculates the band width
    let band_width = trans_df[`${con.HIGHER_BAND}_${con.PERCENTAGES}`].sub(trans_df[`${con.LOWER_BAND}_${con.PERCENTAGES}`])
    let difference = trans_df[con.COVERAGE_PERCENTAGE].sub(trans_df[`${con.LOWER_BAND}_${con.PERCENTAGES}`]).div(band_width)

    // Adjusts Possible Values
    difference = difference.mul(con.POSITION_IN_BANDS_ARRAY.length - 2)

    // Adjusts the extremes
    difference = difference.map((v) => v < 0 ? -1 : v)
    difference = difference.map((v) => v > con.POSITION_IN_BANDS_ARRAY.length - 2 ? con.POSITION_IN_BANDS_ARRAY.length - 2 : v)

    
    // Construct index
    let ind = difference.map((v) => Math.floor(v) + 1)

    // Construct Positions
    let pos = ind.map((i) => con.POSITION_IN_BANDS_ARRAY[i])

    // Adds column
    trans_df = trans_df.addColumn(con.POSITION_IN_BANDS, pos)

    return(trans_df)

    
}


// Create Summary
const createSummary = async (trans_df, modelParameters) =>{

    // Unlinks
    trans_df = trans_df.copy()

    // Drops String columns
    trans_df = trans_df.drop({ columns: [con.POSITION_IN_BANDS,con.PERIOD]})

    // Sets constant date
    trans_df = setColumn(trans_df, con.DATE, Array(trans_df.shape[0]).fill(con.TOTAL)) 

    // Applies group by
    trans_df = groupByDate(trans_df)
    let coverage_percentage = computeDirectCoverageAverage(trans_df)
    trans_df = setColumn(trans_df, con.COVERAGE_PERCENTAGE, coverage_percentage)
        
    // Adjust for bands        
    let [lBand, mlBand, mBand, mhBand, hBand] = getSummaryBands(modelParameters[con.MID_BAND_VALUES], modelParameters[con.BAND_WIDTH])

    trans_df = setColumn(trans_df, `${con.HIGHER_BAND}_${con.PERCENTAGES}`, [hBand])
    trans_df = setColumn(trans_df, `${con.MID_LOW_BAND}_${con.PERCENTAGES}`, [mlBand])
    trans_df = setColumn(trans_df, `${con.MID_BAND}_${con.PERCENTAGES}`, [mBand])
    trans_df = setColumn(trans_df, `${con.MID_HIGH_BAND}_${con.PERCENTAGES}`, [mhBand])
    trans_df = setColumn(trans_df, `${con.LOWER_BAND}_${con.PERCENTAGES}`, [lBand])
    
    // Computes String columns
    trans_df = attachPositionInBands(trans_df) 
    trans_df = trans_df.addColumn(con.PERIOD, [con.TOTAL])



    // Converts to JSON
    let resp = dfd.toJSON(trans_df, { download: false })[0]

    return(resp)

}



export const nonZeroTranactionFunction = (ob) => {

    return(ob[con.NET_EXPOSURE_AMOUNT] !== 0 || ob[con.COVERAGE_AMOUNT] !== 0)
}


export const buildDefaultRow = (columnArray) =>
{
  
    let response = Object.fromEntries(columnArray.map((col) => {
      
        var val = getDefaultValue(col.type)
        if(con.DEFAULT_VALUE in col)
          val = (typeof col[con.DEFAULT_VALUE] === 'function') ? col[con.DEFAULT_VALUE]() : col[con.DEFAULT_VALUE]
    
        return([col[con.ID], val])        
      }))


    return(response)
}

export const getAverageExpiryDay = (current_date, transactions) =>
{
    let amount_days = Object.values(transactions).map((d) => dateFun.daysBetweenDates(current_date, d[con.EXPIRATION_DATE]))
    amount_days = amount_days.filter( d => d >= 0)

    return(amount_days.length === 0 ? 0 : average(amount_days))
}



// Columns
export const allTransactionsCol = 
{ [con.PERIOD] : { [con.ID] : con.PERIOD, [con.NAME] : 'PERIODO', [con.TYPE] : con.TEXT},
  [con.USERNAME] : { [con.ID] : con.USERNAME, [con.NAME] : 'NOMBRE USUARIO', [con.TYPE] : con.TEXT},
  [con.ADVISOR] : { [con.ID] : con.ADVISOR, [con.NAME] : 'NOMBRE ASESOR', [con.TYPE] : con.TEXT},  
  [con.LINE] : { [con.ID] : con.LINE, [con.NAME] : 'LINEA', [con.TYPE] : con.TEXT},
  [con.DATE] : { [con.ID] : con.DATE, [con.NAME] : 'FECHA', [con.TYPE] : con.DATE},
  [con.EXPOSURE_AMOUNT] : { [con.ID] : con.EXPOSURE_AMOUNT, [con.NAME] : 'EXP. (MILES)', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.DENOMINATOR] : con.THOUSAND, allowDecimals : true},
  [con.COVERAGE_AMOUNT] : { [con.ID] : con.COVERAGE_AMOUNT, [con.NAME] : 'COB. (MILES)', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.DENOMINATOR] : con.THOUSAND, allowDecimals : true},                      
  [con.RATE] : { [con.ID] : con.RATE, [con.NAME] : 'TASA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [con.FWD_COVERAGE_AMOUNT] : { [con.ID] : con.FWD_COVERAGE_AMOUNT, [con.NAME] : 'COB. FWD', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.DENOMINATOR] : con.THOUSAND, allowDecimals : true},
  [con.FWD_COVERAGE_RATE] : { [con.ID] : con.FWD_COVERAGE_RATE, [con.NAME] : 'TASA FWD', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [con.DIRECT_DELTA] : { [con.ID] : con.DIRECT_DELTA, [con.NAME] : 'COB. DELTA', [con.TYPE] :con.PERCENTAGE},
  [con.COUNTER_DELTA] : { [con.ID] : con.COUNTER_DELTA, [con.NAME] : 'DELTA CONT.', [con.TYPE] :con.PERCENTAGE},
  [con.OPTION_COVERAGE_AMOUNT] : { [con.ID] : con.OPTION_COVERAGE_AMOUNT, [con.NAME] : 'COB. OPC.', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.DENOMINATOR] : con.THOUSAND, allowDecimals : true},
  [con.OPTION_COVERAGE_RATE] : { [con.ID] : con.OPTION_COVERAGE_RATE, [con.NAME] : 'TASA OPC.', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [con.COUNTER_COVERAGE_AMOUNT] : { [con.ID] : con.COUNTER_COVERAGE_AMOUNT, [con.NAME] : 'COB. CONTRARIAS', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD},
  [con.NET_EXPOSURE_AMOUNT] : { [con.ID] : con.NET_EXPOSURE_AMOUNT, [con.NAME] : 'EXP. NETA  (MILES)', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.DENOMINATOR] : con.THOUSAND, allowDecimals : true}, 
  [con.COVERAGE_PERCENTAGE] : {[con.ID] : con.COVERAGE_PERCENTAGE, [con.NAME] : '% COB.', [con.TYPE] : con.PERCENTAGE} ,
  [con.VAR_RATE]: { [con.ID] : con.VAR_RATE, [con.NAME] : 'TASA VAR', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP },
  [con.EFFECTIVE_RATE] : { [con.ID] : con.EFFECTIVE_RATE,[con.NAME] : 'TASA EFECTIVA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [con.VAR_X_USD] : { [con.ID] : con.VAR_X_USD, [con.NAME] : 'VAR POR USD', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
//   [con.COUNTER_VAR_X_USD] : { [con.ID] : con.COUNTER_VAR_X_USD, [con.NAME] : 'VAR POR USD (CONTRA)', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},  //LEGACY
  [con.VAR] : { [con.ID] : con.VAR, [con.NAME] : 'VAR OPORTU. (MILLONES)', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.DENOMINATOR] : con.MILLION, allowDecimals : true},
  [`${con.LOWER_BAND}_${con.PERCENTAGES}`] : { [con.ID] : `${con.LOWER_BAND}_${con.PERCENTAGES}`, [con.NAME] : '% OBJ. BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.MID_LOW_BAND}_${con.PERCENTAGES}`] : { [con.ID] : `${con.MID_LOW_BAND}_${con.PERCENTAGES}`, [con.NAME] : '% OBJ. BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.MID_BAND}_${con.PERCENTAGES}`] : { [con.ID] : `${con.MID_BAND}_${con.PERCENTAGES}`, [con.NAME] : '% OBJ. BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.MID_HIGH_BAND}_${con.PERCENTAGES}`] : { [con.ID] : `${con.MID_HIGH_BAND}_${con.PERCENTAGES}`, [con.NAME] : '% OBJ. BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.HIGHER_BAND}_${con.PERCENTAGES}`] : { [con.ID] : `${con.HIGHER_BAND}_${con.PERCENTAGES}`, [con.NAME] : '% OBJ. BANDA', [con.TYPE] : con.PERCENTAGE },

  [`${con.LOWER_BAND}_${con.PERCENTAGE_TO_REACH}`] : { [con.ID] : `${con.LOWER_BAND}_${con.PERCENTAGE_TO_REACH}`, [con.NAME] : '% PARA LLEGAR A BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.MID_LOW_BAND}_${con.PERCENTAGE_TO_REACH}`] : { [con.ID] : `${con.MID_LOW_BAND}_${con.PERCENTAGE_TO_REACH}`, [con.NAME] : '% PARA LLEGAR A BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.MID_BAND}_${con.PERCENTAGE_TO_REACH}`] : { [con.ID] : `${con.MID_BAND}_${con.PERCENTAGE_TO_REACH}`, [con.NAME] : '% PARA LLEGAR A BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.MID_HIGH_BAND}_${con.PERCENTAGE_TO_REACH}`] : { [con.ID] : `${con.MID_HIGH_BAND}_${con.PERCENTAGE_TO_REACH}`, [con.NAME] : '% PARA LLEGAR A BANDA', [con.TYPE] : con.PERCENTAGE },
  [`${con.HIGHER_BAND}_${con.PERCENTAGE_TO_REACH}`] : { [con.ID] : `${con.HIGHER_BAND}_${con.PERCENTAGE_TO_REACH}`, [con.NAME] : '% PARA LLEGAR A BANDA', [con.TYPE] : con.PERCENTAGE },

  [`${con.LOWER_BAND}_${con.AMOUNT_TO_REACH}`] : { [con.ID] : `${con.LOWER_BAND}_${con.AMOUNT_TO_REACH}`, [con.NAME] : 'MONTO PARA LLEGAR A BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD},
  [`${con.MID_LOW_BAND}_${con.AMOUNT_TO_REACH}`] : { [con.ID] : `${con.MID_LOW_BAND}_${con.AMOUNT_TO_REACH}`, [con.NAME] : 'MONTO PARA LLEGAR A BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD},
  [`${con.MID_BAND}_${con.AMOUNT_TO_REACH}`] : { [con.ID] : `${con.MID_BAND}_${con.AMOUNT_TO_REACH}`, [con.NAME] : 'MONTO PARA LLEGAR A BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD},
  [`${con.MID_HIGH_BAND}_${con.AMOUNT_TO_REACH}`] : { [con.ID] : `${con.MID_HIGH_BAND}_${con.AMOUNT_TO_REACH}`, [con.NAME] : 'MONTO PARA LLEGAR A BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD},
  [`${con.HIGHER_BAND}_${con.AMOUNT_TO_REACH}`] : { [con.ID] : `${con.HIGHER_BAND}_${con.AMOUNT_TO_REACH}`, [con.NAME] : 'MONTO PARA LLEGAR A BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_USD},
        
  [con.POSITION_IN_BANDS] : { [con.ID] : con.POSITION_IN_BANDS, [con.NAME] : 'Posición Banda', [con.TYPE] : con.TEXT, [con.DEFAULT_VALUE]: con.POSITION_IN_BANDS_ARRAY[0], [con.COMPRESS] : (val) => typeof val === 'string' ? val.replace("Banda", "B.") : val},            
         
  [`${con.LOWER_BAND}_${con.VAR_X_USD}`] : { [con.ID] : `${con.LOWER_BAND}_${con.VAR_X_USD}`, [con.NAME] : 'VAR POR USD BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [`${con.MID_LOW_BAND}_${con.VAR_X_USD}`] : { [con.ID] : `${con.MID_LOW_BAND}_${con.VAR_X_USD}`, [con.NAME] : 'VAR POR USD BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [`${con.MID_BAND}_${con.VAR_X_USD}`] : { [con.ID] : `${con.MID_BAND}_${con.VAR_X_USD}`, [con.NAME] : 'VAR POR USD BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [`${con.MID_HIGH_BAND}_${con.VAR_X_USD}`] : { [con.ID] : `${con.MID_HIGH_BAND}_${con.VAR_X_USD}`, [con.NAME] : 'VAR POR USD BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
  [`${con.HIGHER_BAND}_${con.VAR_X_USD}`] : { [con.ID] : `${con.HIGHER_BAND}_${con.VAR_X_USD}`, [con.NAME] : 'VAR POR USD BANDA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP},
                      
  [`${con.LOWER_BAND}_${con.VAR}`] : { [con.ID] : `${con.LOWER_BAND}_${con.VAR}`, [con.NAME] : 'VAR BANDA (MILLONES)', [con.TYPE] : con.MONEY,  [con.CURRENCY] : con.MONEY_COP, [con.DENOMINATOR] : con.MILLION, allowDecimals : true },
  [`${con.MID_LOW_BAND}_${con.VAR}`] : { [con.ID] : `${con.MID_LOW_BAND}_${con.VAR}`, [con.NAME] : 'VAR BANDA (MILLONES)', [con.TYPE] : con.MONEY,  [con.CURRENCY] : con.MONEY_COP, [con.DENOMINATOR] : con.MILLION, allowDecimals : true },
  [`${con.MID_BAND}_${con.VAR}`] : { [con.ID] : `${con.MID_BAND}_${con.VAR}`, [con.NAME] : 'VAR BANDA (MILLONES)', [con.TYPE] : con.MONEY,  [con.CURRENCY] : con.MONEY_COP, [con.DENOMINATOR] : con.MILLION, allowDecimals : true },
  [`${con.MID_HIGH_BAND}_${con.VAR}`] : { [con.ID] : `${con.MID_HIGH_BAND}_${con.VAR}`, [con.NAME] : 'VAR BANDA (MILLONES)', [con.TYPE] : con.MONEY,  [con.CURRENCY] : con.MONEY_COP, [con.DENOMINATOR] : con.MILLION, allowDecimals : true },
  [`${con.HIGHER_BAND}_${con.VAR}`] : { [con.ID] : `${con.HIGHER_BAND}_${con.VAR}`, [con.NAME] : 'VAR BANDA (MILLONES)', [con.TYPE] : con.MONEY,  [con.CURRENCY] : con.MONEY_COP, [con.DENOMINATOR] : con.MILLION, allowDecimals : true },

  // Summary
  [con.EXPIRY_DAYS] : {[con.ID] : con.EXPIRY_DAYS, [con.NAME] : 'VENCIMIENTO PROMEDIO (DIAS)', [con.TYPE] : con.NUMERIC, [con.EDITABLE] : false},
  [con.LAST_EDITED] : { [con.ID] : con.LAST_EDITED, [con.NAME] : 'ÚLTIMA EDICIÓN', [con.TYPE] : con.DATE_TIME},
  [con.OBJECTIVE_BAND] : { [con.ID] : con.OBJECTIVE_BAND, [con.NAME] : 'BANDA OBJETIVO', [con.TYPE] : con.TEXT },
  [con.OBJECTIVE_BAND_PERCENTAGE] : { [con.ID] : con.OBJECTIVE_BAND_PERCENTAGE, [con.NAME] : '% BANDA OBJETIVO', [con.TYPE] : con.PERCENTAGE },


}


export const selectedTransactionCols = [{ [con.ID] : con.DATE},
                                        { [con.ID] : con.EXPOSURE_AMOUNT},
                                        { [con.ID] : con.COVERAGE_AMOUNT},                      
                                        { [con.ID] : con.RATE},
                                        { [con.ID] : con.FWD_COVERAGE_AMOUNT},
                                        { [con.ID] : con.FWD_COVERAGE_RATE},
                                        { [con.ID] : con.DIRECT_DELTA},
                                        { [con.ID] : con.OPTION_COVERAGE_AMOUNT},
                                        { [con.ID] : con.OPTION_COVERAGE_RATE},
                                        { [con.ID] : con.COUNTER_DELTA},
                                        { [con.ID] : con.COUNTER_COVERAGE_AMOUNT},
                                        { [con.ID] : con.NET_EXPOSURE_AMOUNT}, 
                                        { [con.ID] : con.COVERAGE_PERCENTAGE} ,
                                        { [con.ID] : con.VAR_RATE},
                                        { [con.ID] : con.EFFECTIVE_RATE},
                                        { [con.ID] : con.VAR_X_USD},
                                        { [con.ID] : con.VAR}]
                                        // { [con.ID] : con.COUNTER_VAR_X_USD}, //
                                        // { [con.ID] : `${con.LOWER_BAND}_${con.PERCENTAGES}`},
                                        // { [con.ID] : `${con.MID_LOW_BAND}_${con.PERCENTAGES}`},
                                        // { [con.ID] : `${con.MID_BAND}_${con.PERCENTAGES}`},
                                        // { [con.ID] : `${con.MID_HIGH_BAND}_${con.PERCENTAGES}`},
                                        // { [con.ID] : `${con.HIGHER_BAND}_${con.PERCENTAGES}`},

                                        // { [con.ID] : `${con.LOWER_BAND}_${con.AMOUNT_TO_REACH}`},
                                        // { [con.ID] : `${con.MID_LOW_BAND}_${con.AMOUNT_TO_REACH}`},
                                        // { [con.ID] : `${con.MID_BAND}_${con.AMOUNT_TO_REACH}`},
                                        // { [con.ID] : `${con.MID_HIGH_BAND}_${con.AMOUNT_TO_REACH}`},
                                        // { [con.ID] : `${con.HIGHER_BAND}_${con.AMOUNT_TO_REACH}`}, 
                                                                
                                        // { [con.ID] : `${con.LOWER_BAND}_${con.VAR}`},
                                        // { [con.ID] : `${con.MID_LOW_BAND}_${con.VAR}`},
                                        // { [con.ID] : `${con.MID_BAND}_${con.VAR}`},
                                        // { [con.ID] : `${con.MID_HIGH_BAND}_${con.VAR}`},
                                        // { [con.ID] : `${con.HIGHER_BAND}_${con.VAR}`}]


export const mobileSelectedTransactionCols =  [{ [con.ID] : con.DATE},
                                                { [con.ID] : con.EXPOSURE_AMOUNT},
                                                { [con.ID] : con.COVERAGE_AMOUNT},                      
                                                { [con.ID] : con.RATE},                             
                                                { [con.ID] : con.COVERAGE_PERCENTAGE} ,
                                                { [con.ID] : con.POSITION_IN_BANDS},

                                                { [con.ID] : `${con.LOWER_BAND}_${con.VAR_X_USD}`},
                                                { [con.ID] : `${con.MID_LOW_BAND}_${con.VAR_X_USD}`},
                                                { [con.ID] : `${con.MID_BAND}_${con.VAR_X_USD}`},
                                                { [con.ID] : `${con.MID_HIGH_BAND}_${con.VAR_X_USD}`},
                                                { [con.ID] : `${con.HIGHER_BAND}_${con.VAR_X_USD}`},
                                                                    
                                                { [con.ID] : `${con.LOWER_BAND}_${con.VAR}`},
                                                { [con.ID] : `${con.MID_LOW_BAND}_${con.VAR}`},
                                                { [con.ID] : `${con.MID_BAND}_${con.VAR}`},
                                                { [con.ID] : `${con.MID_HIGH_BAND}_${con.VAR}`},
                                                { [con.ID] : `${con.HIGHER_BAND}_${con.VAR}`},
                                                ]


// Exposure Columns
export const allExposureColumns = {
    [con.EXPIRATION_DATE] : {[con.ID] : con.EXPIRATION_DATE, [con.TYPE] :  con.DATE, [con.DEFAULT_VALUE] :  () => dateFun.formatDate(dateFun.getNextWorkingDay()),[con.NAME] : "Fecha"},
    [con.AMOUNT] : { [con.ID] : con.AMOUNT, [con.TYPE] :  con.MONEY, [con.NAME] : "MONTO", [con.CURRENCY] : con.MONEY_USD, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0 },
    [con.OPENING_TRM] : {[con.ID] : con.OPENING_TRM, [con.TYPE] :  con.MONEY, [con.NAME] : "TRM APERTURA", [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0},
    [con.COMMENT] : { [con.ID] : con.COMMENT, [con.TYPE] :  con.TEXT, [con.NAME] : "Comentario", [con.DEFAULT_VALUE] :  () => con.BLANK },
    [con.STATE] : { [con.ID] : con.STATE, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.EDITABLE] : false, [con.NAME] : "Estado", [con.DEFAULT_VALUE] : con.ACTIVE, [con.OPTIONS] : [con.ACTIVE, con.EXPIRED], [con.VALUE_TRANSLATOR] : translateParameter}
}

export const selectedExposureColumns = [ {[con.ID] : con.EXPIRATION_DATE},
                                        { [con.ID] : con.AMOUNT},
                                        { [con.ID] : con.OPENING_TRM},
                                        { [con.ID] : con.COMMENT},
                                        { [con.ID] : con.STATE}]

// Forward Coverage Columns
export const allForwardCoverageColumns = {
    [con.EXPIRATION_DATE] : { [con.ID] : con.EXPIRATION_DATE, [con.TYPE] :  con.DATE,  [con.DEFAULT_VALUE] :  () => dateFun.formatDate(dateFun.getNextWorkingDay()), [con.NAME] : "FECHA VENCIMIENTO"},
    [con.OPENING_DATE] : { [con.ID] : con.OPENING_DATE, [con.TYPE] :  con.DATE, [con.NAME] : "FECHA APERTURA",  [con.DEFAULT_VALUE] :  () => dateFun.isWorkDay(dateFun.getToday()) ? dateFun.formatDate(dateFun.getToday()) : dateFun.formatDate(dateFun.getNextWorkingDay()), [con.SHOULD_DISABLE_DATE] : (date)=> !dateFun.isWorkDay(date) || date > dateFun.getToday()},
    [con.AMOUNT] : {[con.ID] : con.AMOUNT, [con.TYPE] :  con.MONEY, [con.NAME] : "MONTO", [con.CURRENCY]: con.MONEY_USD, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0 },
    [con.RATE] : { [con.ID] : con.RATE,[con.TYPE] :  con.MONEY, [con.NAME] : "TASA FORWARD", [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true , [con.DEFAULT_VALUE] :  () => 0 },
    [con.OPENING_SPOT] : {[con.ID] : con.OPENING_SPOT, [con.TYPE] :  con.MONEY, [con.NAME] : "SPOT APERTURA", [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] : () => getMarketValue(con.SPOT) },
    [con.COVERAGE_TYPE] : { [con.ID] : con.COVERAGE_TYPE, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.EDITABLE] : true, [con.NAME] : "TIPO", [con.DEFAULT_VALUE] : con.BUY, [con.OPTIONS] : [con.BUY, con.SELL], [con.VALUE_TRANSLATOR] : translateParameter},
    [con.COMMENT] : { [con.ID] : con.COMMENT, [con.TYPE] :  con.TEXT, [con.NAME] : "Comentario", [con.DEFAULT_VALUE] :  () => con.BLANK },
    [con.COUNTERPARTY] : { [con.ID] : con.COUNTERPARTY, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.OPTIONS] : con.COUNTERPARTIES, [con.NAME] : "Contraparte", [con.VALUE_TRANSLATOR] : counterpartiesNames, [con.DEFAULT_VALUE] :  () => con.BLANK },
    [con.STATE] : { [con.ID] : con.STATE, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.EDITABLE] : false, [con.NAME] : "Estado", [con.DEFAULT_VALUE] : con.ACTIVE, [con.OPTIONS] : [con.ACTIVE, con.EXPIRED], [con.VALUE_TRANSLATOR] : translateParameter},

    // Forward Assessment
    [con.EXPIRY_DAYS] : {[con.ID] : con.EXPIRY_DAYS, [con.NAME] : 'DIAS PARA VENCIMIENTO', [con.TYPE] : con.NUMERIC, [con.EDITABLE] : false},
    [con.DEVALUATION_VALUE] : {[con.ID] : con.DEVALUATION_VALUE, [con.NAME] : 'CURVA DE DEV.', [con.TYPE] : con.PERCENTAGE, [con.EDITABLE] : false, [con.DECIMAL_SCALE] : 2},
    [con.DISCOUNT_RATE] : {[con.ID] : con.DISCOUNT_RATE, [con.NAME] : 'TASA DESCUENTO', [con.TYPE] : con.PERCENTAGE, [con.EDITABLE] : false, [con.DECIMAL_SCALE] : 2},
    [con.NEW_FORWARD_RATE] : {[con.ID] : con.NEW_FORWARD_RATE, [con.NAME] : 'TASA VAL. FUTURA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false, [con.ALLOW_DECIMALS] : true },
    [con.FORWARD_RATE_DIFFERENCE] : {[con.ID] : con.FORWARD_RATE_DIFFERENCE, [con.NAME] : 'DIF. FWD', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false, [con.ALLOW_DECIMALS] : true },
    [con.FORWARD_RATE_DIFFERENCE_PRESENT_VALUE] : {[con.ID] : con.FORWARD_RATE_DIFFERENCE_PRESENT_VALUE, [con.NAME] : 'DIF. VALOR PRESENTE', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false, [con.ALLOW_DECIMALS] : true },
    [con.ASSESSMENT] : {[con.ID] : con.ASSESSMENT, [con.NAME] : 'VALORACIÓN', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false},

}

export const selectedForwardCoverageColumns = [ { [con.ID] : con.EXPIRATION_DATE },
                                                { [con.ID] : con.OPENING_DATE },
                                                { [con.ID] : con.AMOUNT },
                                                { [con.ID] : con.RATE },
                                                { [con.ID] : con.OPENING_SPOT },
                                                { [con.ID] : con.COVERAGE_TYPE },
                                                { [con.ID] : con.COUNTERPARTY },
                                                { [con.ID] : con.COMMENT},
                                                { [con.ID] : con.STATE}]


// Spot Coverage Columns
export const allSpotCoverageColumns = {
    
    [con.OPENING_DATE] : { [con.ID] : con.OPENING_DATE, [con.NAME] : 'Fecha Compra', [con.TYPE] :  con.DATE, [con.DEFAULT_VALUE] :  () => dateFun.isWorkDay(dateFun.getToday()) ? dateFun.formatDate(dateFun.getToday()) : dateFun.formatDate(dateFun.getPreviousWorkingDay()), [con.SHOULD_DISABLE_DATE] : (date)=> !dateFun.isWorkDay(date) || date > dateFun.getToday()},
    [con.AMOUNT] : { [con.ID] : con.AMOUNT, [con.NAME] : 'Monto', [con.TYPE] :  con.MONEY, [con.CURRENCY] : 'USD', [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0 },
    [con.RATE] : { [con.ID] : con.RATE, [con.NAME] : "Tasa Compra", [con.TYPE] :  con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] : () => getMarketValue(con.SPOT)},
    [con.COMMENT] : {[con.ID] : con.COMMENT,  [con.NAME] : 'Comentario', [con.TYPE] :  con.TEXT, [con.DEFAULT_VALUE] :  () => con.BLANK },
    [con.SPOT_COVERAGE_TYPE] : { [con.ID] : con.SPOT_COVERAGE_TYPE, [con.NAME] : 'Tipo', [con.TYPE] :  con.MULTIPLE_SELECTION, [con.DEFAULT_VALUE] : con.CASH_REGISTER, [con.OPTIONS] : [con.CASH_REGISTER], [con.EDITABLE] : false, [con.VALUE_TRANSLATOR] : translateParameter }
}      

export const selectedSpotCoverageColumns = [    { [con.ID] : con.OPENING_DATE },
                                                { [con.ID] : con.AMOUNT },
                                                { [con.ID] : con.RATE },
                                                { [con.ID] : con.COMMENT},
                                                { [con.ID] : con.SPOT_COVERAGE_TYPE}]

// Option Coverage Columns
export const allOptionCoverageColumns = {

    [con.EXPIRATION_DATE] : { [con.ID] : con.EXPIRATION_DATE, [con.TYPE] :  con.DATE,  [con.DEFAULT_VALUE] :  () => dateFun.formatDate(dateFun.getNextWorkingDay()), [con.NAME] : "FECHA VENCIMIENTO"},
    [con.OPENING_DATE] : { [con.ID] : con.OPENING_DATE, [con.NAME] : 'Fecha Compra', [con.TYPE] :  con.DATE, [con.DEFAULT_VALUE] :  () => dateFun.isWorkDay(dateFun.getToday()) ? dateFun.formatDate(dateFun.getToday()) : dateFun.formatDate(dateFun.getPreviousWorkingDay()), [con.SHOULD_DISABLE_DATE] : (date)=> !dateFun.isWorkDay(date) || date > dateFun.getToday()},
    [con.AMOUNT] : { [con.ID] : con.AMOUNT, [con.NAME] : 'Monto', [con.TYPE] :  con.MONEY, [con.CURRENCY] : 'USD', [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0  },
    [con.OPENING_SPOT] : {[con.ID] : con.OPENING_SPOT, [con.TYPE] :  con.MONEY, [con.NAME] : "SPOT", [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] : () => getMarketValue(con.SPOT) },
    [con.STRIKE] : { [con.ID] : con.STRIKE, [con.NAME] : "Strike", [con.TYPE] :  con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0 },
    [con.PREMIUM] : { [con.ID] : con.PREMIUM, [con.NAME] : "Prima", [con.TYPE] :  con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.ALLOW_DECIMALS] : true, [con.DEFAULT_VALUE] :  () => 0 },
    [con.OPTION_TYPE] : { [con.ID] : con.OPTION_TYPE, [con.NAME] : 'Call/Put', [con.TYPE] :  con.MULTIPLE_SELECTION, [con.DEFAULT_VALUE] : con.CALL, [con.OPTIONS] : [con.CALL, con.PUT], [con.EDITABLE] : true, [con.VALUE_TRANSLATOR] : translateParameter },
    [con.COVERAGE_TYPE] : { [con.ID] : con.COVERAGE_TYPE, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.EDITABLE] : true, [con.NAME] : "TIPO", [con.DEFAULT_VALUE] : con.BUY, [con.OPTIONS] : [con.BUY, con.SELL], [con.VALUE_TRANSLATOR] : translateParameter},
    [con.COUNTERPARTY] : { [con.ID] : con.COUNTERPARTY, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.OPTIONS] : con.COUNTERPARTIES, [con.NAME] : "Contraparte", [con.VALUE_TRANSLATOR] : counterpartiesNames, [con.DEFAULT_VALUE] :  () => con.BLANK },
    [con.COMMENT] : {[con.ID] : con.COMMENT,  [con.NAME] : 'Comentario', [con.TYPE] :  con.TEXT, [con.DEFAULT_VALUE] :  () => con.BLANK },
    [con.STATE] : { [con.ID] : con.STATE, [con.TYPE] :  con.MULTIPLE_SELECTION, [con.EDITABLE] : false, [con.NAME] : "Estado", [con.DEFAULT_VALUE] : con.ACTIVE, [con.OPTIONS] : [con.ACTIVE, con.EXPIRED], [con.VALUE_TRANSLATOR] : translateParameter},
    
    // Option Assessment
    [con.EXPIRY_DAYS] : { [con.ID] : [con.EXPIRY_DAYS], [con.NAME] : 'DIAS VTO', [con.TYPE] : con.NUMERIC, [con.EDITABLE] : false },
    [con.EXPIRE_YEARS] : { [con.ID] : [con.EXPIRE_YEARS], [con.NAME] : 'AÑOS PARA VENCIMEINTO' },
    [con.FOREIGN_RATE] : { [con.ID] : [con.FOREIGN_RATE], [con.NAME] : 'TASA EXT', [con.TYPE] : con.PERCENTAGE, [con.EDITABLE] : false, [con.DECIMAL_SCALE] : 2},
    [con.DISCOUNT_RATE] : { [con.ID] : [con.DISCOUNT_RATE], [con.NAME] : 'TASA LOC', [con.TYPE] : con.PERCENTAGE, [con.EDITABLE] : false, [con.DECIMAL_SCALE] : 2 },
    [con.FREE_RISK_RATE] : { [con.ID] : [con.FREE_RISK_RATE], [con.NAME] : 'TASA LIBRE DE RIESGO' },
    [con.OPTION_D1_HIST] : { [con.ID] : [con.OPTION_D1_HIST], [con.NAME] : 'D1 HISTORICO' },
    [con.OPTION_D2_HIST] : { [con.ID] : [con.OPTION_D2_HIST], [con.NAME] : 'D2 HISTORICO' },
    [con.OPTION_N_D1_HIST] : { [con.ID] : [con.OPTION_N_D1_HIST], [con.NAME] : 'NORMAL D1 HISTORICO' },
    [con.OPTION_N_D2_HIST] : { [con.ID] : [con.OPTION_N_D2_HIST], [con.NAME] : 'NORMAL D2 HISTORICO' },
    [con.OPTION_N_NEG_D1_HIST] : { [con.ID] : [con.OPTION_N_NEG_D1_HIST], [con.NAME] : 'NORMAL INVERSO D2 HISTORICO' },
    [con.OPTION_N_NEG_D2_HIST] : { [con.ID] : [con.OPTION_N_NEG_D2_HIST], [con.NAME] : 'NORMAL INVERSO D2 HISTORICO' },
    [con.DELTA_CALL] : { [con.ID] : [con.DELTA_CALL], [con.NAME] : 'DELTA CALL' },
    [con.DELTA_PUT] : { [con.ID] : [con.DELTA_PUT], [con.NAME] : 'DELTA PUT' },
    [con.DELTA] : { [con.ID] : [con.DELTA], [con.NAME] : 'DELTA', [con.TYPE] : con.PERCENTAGE, [con.EDITABLE] : false, [con.DECIMAL_SCALE] : 2 },
    [con.VOLATILITY_CURVE] : { [con.ID] : [con.VOLATILITY_CURVE], [con.NAME] : 'CURVA VOL', [con.TYPE] : con.PERCENTAGE, [con.EDITABLE] : false, [con.DECIMAL_SCALE] : 2},
    [con.OPTION_D1_VOL] : { [con.ID] : [con.OPTION_D1_VOL], [con.NAME] : 'D1 VOLATILIDAD' },
    [con.OPTION_D2_VOL] : { [con.ID] : [con.OPTION_D2_VOL], [con.NAME] : 'D2 VOLATILIDAD' },
    [con.OPTION_N_D1_VOL] : { [con.ID] : [con.OPTION_N_D1_VOL], [con.NAME] : 'NORMAL D2 VOLATILIDAD' },
    [con.OPTION_N_D2_VOL] : { [con.ID] : [con.OPTION_N_D2_VOL], [con.NAME] : 'NORMAL D2 VOLATILIDAD' },
    [con.OPTION_VALUE] : { [con.ID] : [con.OPTION_VALUE], [con.NAME] : 'VALORACIÓN DE LA OPCION' },
    [con.ASSESSMENT] : { [con.ID] : [con.ASSESSMENT], [con.NAME] : 'VALORACIÓN', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false },
    [con.PREMIUM_GAINS] : { [con.ID] : [con.PREMIUM_GAINS], [con.NAME] : 'DIRECCION PRIMA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false },
    [con.PREMIUM_BALANCE] : { [con.ID] : [con.PREMIUM_BALANCE], [con.NAME] : 'BALANCE PRIMA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false },
    [con.TOTAL_ASSESSMENT] : { [con.ID] : [con.TOTAL_ASSESSMENT], [con.NAME] : 'VALORACIÓN + PRIMA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false },
    [con.ASSESSMENT_RATE] : { [con.ID] : [con.ASSESSMENT_RATE], [con.NAME] : 'TASA', [con.TYPE] : con.MONEY, [con.CURRENCY] : con.MONEY_COP, [con.EDITABLE] : false },

}      

export const selectedOptionCoverageColumns = [ { [con.ID] : con.EXPIRATION_DATE },
                                               { [con.ID] : con.OPENING_DATE },
                                               { [con.ID] : con.AMOUNT },
                                               { [con.ID] : con.OPENING_SPOT },
                                               { [con.ID] : con.STRIKE },
                                               { [con.ID] : con.PREMIUM },
                                               { [con.ID] : con.OPTION_TYPE },
                                               { [con.ID] : con.COVERAGE_TYPE },
                                               { [con.ID] : con.COUNTERPARTY },
                                               { [con.ID] : con.COMMENT},
                                               { [con.ID] : con.STATE}]


// Alert columns
export const allAlertMarketColumns = {
    [con.ACCOUNT_TYPE] : {[con.NAME] : 'Tipo', [con.TYPE] :  con.MULTIPLE_SELECTION, [con.ID] : con.ACCOUNT_TYPE, [con.OPTIONS] : ['Cuenta de Compensación','Cobertura Natural'], [con.VALUE_TRANSLATOR] : translateParameter, [con.EDITABLE] : false },
    [con.COMMENT] :{ [con.NAME] : 'Comentario',  [con.TYPE] :  con.TEXT, [con.ID] : con.COMMENT},
    [con.AMOUNT] : { [con.ID] : con.AMOUNT, [con.NAME] : 'Monto', [con.TYPE] :  con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.ALLOW_DECIMALS] : true}
}

export const selectedMarketAlertColumns = [{ [con.ID] : con.ACCOUNT_TYPE },
                                           { [con.ID] : con.COMMENT },
                                           { [con.ID] : con.AMOUNT }]


// Account columns
export const allAccountColumns = {
    [con.ACCOUNT_TYPE] : {[con.NAME] : 'Tipo', [con.TYPE] :  con.MULTIPLE_SELECTION, [con.ID] : con.ACCOUNT_TYPE, [con.OPTIONS] : ['Cuenta de Compensación','Cobertura Natural'], [con.VALUE_TRANSLATOR] : translateParameter, [con.EDITABLE] : false },
    [con.COMMENT] :{ [con.NAME] : 'Comentario',  [con.TYPE] :  con.TEXT, [con.ID] : con.COMMENT},
    [con.AMOUNT] : { [con.ID] : con.AMOUNT, [con.NAME] : 'Monto', [con.TYPE] :  con.MONEY, [con.CURRENCY] : con.MONEY_USD, [con.ALLOW_DECIMALS] : true}
}

export const selectedAccountColumns = [{ [con.ID] : con.ACCOUNT_TYPE },
                                       { [con.ID] : con.COMMENT },
                                       { [con.ID] : con.AMOUNT }]



export const prepareForExport = (transactionSummaryObject, selectedColumns) => {

    const arr = Object.values(transactionSummaryObject).map(ob => {
        let newOb = {}
        // Adjust Column Names
        selectedColumns.forEach(col => {

            // Checks if value needs translation
            let translate = (val) => val
            if(col[con.TYPE] === con.MULTIPLE_SELECTION)
                translate = col[con.VALUE_TRANSLATOR]
            
            newOb[col[con.NAME]] = translate(ob[col[con.ID]])
        })
            
        return(newOb)
    })

    return(arr)


}

// Support Methods
// ---------------------------

// Create empty dataFrame
const createEmptyDataFrame = () =>
{
    // NOTE: At the time of implementation, there is no way to create an empty dataframe (I know!!!).
    // This method will return a null value for now.

    return(null)
}

const sortByDate = (df, date_column) =>
{   
     // NOTE: At the time of implementation, there is a bug when sorting by date
     // This method does it manually, but can be overwritten with the proper method once the bug is fixed
    const temp_col = "TEMPORAL___COLUMN"    


    // Creates dummy column
    let dateList = df[date_column].dt["$dateObjectArray"]
    let min_date = Math.min(...dateList)
    df = df.addColumn(temp_col, dateList.map((d) => d - min_date));

    // Sorts by the column
    df = df.sortValues(temp_col)

    // Resets index
    df = df.resetIndex()

    // Drops Column
    df = df.drop({columns : [temp_col]})

    return(df)

}

// Method for setting a new column
// This method was created because the addColumn did not behave correctly if the column already existed
const setColumn = (df, col_name, new_values) =>
{
    df = df.drop({columns :[col_name]})
    df = df.addColumn(col_name, new_values)

    return(df)

}


const createMockExposure = (current_date) =>
{
    return({
        [con.EXPIRATION_DATE] : dateFun.formatDate(dateFun.addDays(current_date,1)),
        [con.AMOUNT] : 0,
        [con.COMMENT] : "",
        [con.STATE] : con.ACTIVE
    })
}


const createMockFwdCoverage = (current_date) =>
{
    return({
        [con.EXPIRATION_DATE] : dateFun.formatDate(dateFun.addDays(current_date,1)),
        [con.OPENING_DATE] : dateFun.formatDate(dateFun.addDays(current_date,1)),
        [con.AMOUNT] : 0,
        [con.RATE] : 0,
        [con.OPENING_SPOT] : 0,
        [con.COMMENT] : "",         
        [con.STATE] : con.ACTIVE
    })
}

const createMockOptionCoverage = (current_date) => {
      
        return({
            [con.EXPIRATION_DATE] : dateFun.formatDate(dateFun.addDays(current_date,1)),
            [con.OPENING_DATE] : dateFun.formatDate(dateFun.addDays(current_date,1)),
            [con.COVERAGES] : 0,  
            [con.AMOUNT] : 0,  
            [con.STRIKE] : 0,  
            [con.PREMIUM_GAINS] : 0,  
            [con.STATE] : con.ACTIVE
        })
    }


 const computeDirectCoverageAverage = (trans_df) =>
 {

    let exp = trans_df[con.EXPOSURE_AMOUNT]
    let cov = trans_df[con.COVERAGE_AMOUNT]
    let counter_fwd = trans_df[con.FWD_COUNTER_COVERAGE_AMOUNT]
    let counter_buy_call = trans_df[con.COUNTER_BUY_CALL_DELTA_AMOUNT]
    let counter_sell_call = trans_df[con.COUNTER_SELL_CALL_DELTA_AMOUNT]
    let counter_buy_put = trans_df[con.COUNTER_BUY_PUT_DELTA_AMOUNT]
    let counter_sell_put = trans_df[con.COUNTER_SELL_PUT_DELTA_AMOUNT]
    let counter = counter_fwd.add(counter_buy_call).add(counter_sell_call).add(counter_buy_put).add(counter_sell_put)
    let covMcount = cov.sub(counter)

    // Direct
    let coverage_percentage = covMcount.div(exp.add(epsylon))

    // Sets 0 coverage when exposure is 0
    coverage_percentage = coverage_percentage.mul(exp.gt(0))

    // Sets 1 coverage when coverage is positive and exposures is zero
    coverage_percentage = coverage_percentage.add(covMcount.gt(0).mul(exp.eq(0)))

    // Sets -1 coverage when counter coverage is positive and exposures and coverage is zero
    coverage_percentage = coverage_percentage.add(covMcount.lt(0).mul(-1).mul(exp.eq(0)))

    // coverage_percentage = coverage_percentage.add((counter.mul(-1).div(exp.add(epsylon))).mul(exp.gt(0)))
    
    return (coverage_percentage)
 }


 const computeCounterCoverageAverage = (trans_df) =>
 {

    let exp = trans_df[con.EXPOSURE_AMOUNT]
    let counter_cov = trans_df[con.FWD_COUNTER_COVERAGE_AMOUNT]
  
    let counter_coverage_percentage = exp.sub(counter_cov).div(exp.add(epsylon)).mul(-1).add(1)

    // Sets 0 coverage when exposure is 0
    counter_coverage_percentage = counter_coverage_percentage.mul(exp.gt(0))

    // Sets 1 coverage when coverage is positive and exposures is zero
    counter_coverage_percentage = counter_coverage_percentage.add(counter_cov.gt(0).mul(exp.eq(0)))

    return(counter_coverage_percentage)
 }
 

